// src/component/AdminGroupAllocation2026.js
//
// 2026 グループ分け管理画面（/admin/group-allocation-2026）
//
//  方針：起動時には users コレクションを総ナメしない。
//    - `groupActivity2026Aggregates/regions`（Cloud Functions が維持）から地域別カウントを取得
//    - `groupActivity2026Groups` をロードし、編成済み人数を差し引いて「未編入」を算出
//    - 都道府県をドロップダウンから選んだ時に初めて、その都道府県の users を遅延フェッチ
//      （複合インデックス： groupActivity2026.response + prefecture）
//
//  グループドキュメントは「メンバー情報を denormalize 保存」する：
//    groupActivity2026Groups/{groupId} = {
//      cohort: "normal",
//      region: string,
//      name: string,
//      status: "draft" | "finalized",
//      members: [
//        { uid, name, avatar, prefecture, usState, country,
//          groups2025SecondHalf, paymentPhases: ["2","3","4"] }
//      ],
//      createdAt, updatedAt, finalizedAt?
//    }
//    users/{uid}.groupActivity2026.groupId = string|null   // 逆引き

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Collapse,
  Divider,
  Grid,
  Avatar,
  ListSubheader,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import MaleRoundedIcon from "@mui/icons-material/MaleRounded";
import FemaleRoundedIcon from "@mui/icons-material/FemaleRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import ShuffleRoundedIcon from "@mui/icons-material/ShuffleRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import {
  collection,
  getDoc,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  writeBatch,
  serverTimestamp,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";
import { db, functions, httpsCallable } from "../api/firebase";
import { useNavigate } from "react-router-dom";

import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import { AppThemeContext } from "./AppShell";
import { is4kiPaidUser } from "./groupActivity2026Audience";
import { countries as COUNTRIES_DATA } from "../constants/countries";

const DND_MEMBER_TYPE = "AUTO_ALLOC_MEMBER";

const DEFAULT_GROUP_MIN = 4;
const DEFAULT_GROUP_MAX = 6;

// 日本の地方区分（ドロップダウンのセクションヘッダ用）
const REGION_GROUPS = [
  ["北海道", ["北海道"]],
  ["東北", ["青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県"]],
  [
    "関東",
    ["茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県"],
  ],
  [
    "中部",
    [
      "新潟県", "富山県", "石川県", "福井県", "山梨県",
      "長野県", "岐阜県", "静岡県", "愛知県",
    ],
  ],
  [
    "近畿",
    ["三重県", "滋賀県", "京都府", "大阪府", "兵庫県", "奈良県", "和歌山県"],
  ],
  ["中国", ["鳥取県", "島根県", "岡山県", "広島県", "山口県"]],
  ["四国", ["徳島県", "香川県", "愛媛県", "高知県"]],
  [
    "九州・沖縄",
    [
      "福岡県", "佐賀県", "長崎県", "熊本県",
      "大分県", "宮崎県", "鹿児島県", "沖縄県",
    ],
  ],
];
const JP_PREFS = new Set(REGION_GROUPS.flatMap(([, p]) => p));
const OVERSEAS_PREFIX = "海外（";

// 無法松コホート：2026-06-01 の通常締切を過ぎてから「参加」を希望した駆け込み組。
// ドロップダウンに都道府県とは別枠で出して、リアルタイム集計する。
// 集計対象は groupActivity2026.response == "join" かつ
// groupActivity2026.respondedAt >= 2026-06-21（JST）のユーザー。
const MUHOUMATSU_KEY = "無法松";
const MUHOUMATSU_CUTOFF_DATE = new Date("2026-06-21T00:00:00+09:00");

// ──────────────────────────────────────────────────────────────
// helpers
// ──────────────────────────────────────────────────────────────

function regionKey(u) {
  const pref = (u.prefecture || "").trim();
  if (pref === "海外（アメリカ）") {
    const st = (u.usState || "").trim() || "未登録";
    return `海外（アメリカ）/${st}`;
  }
  if (pref === "海外（その他）") {
    const c = (u.country || "").trim() || "未登録";
    return `海外（その他）/${c}`;
  }
  return pref || "未登録";
}

// prefecture によって「市区町村」「州」「国」のうち適切な値を返す
function getUserLocale(user) {
  const pref = user?.prefecture || "";
  if (pref === "海外（アメリカ）") return user.usState || "";
  if (pref === "海外（その他）") return user.country || "";
  return user.city || "";
}

// 同様にラベル（"市区町村" / "州" / "国"）
function getLocaleLabel(prefectureOrRegionKey) {
  if (prefectureOrRegionKey === "海外（アメリカ）") return "州";
  if (prefectureOrRegionKey === "海外（その他）") return "国";
  return "市区町村";
}

function mmmPhases(payments) {
  const set = new Set();
  (payments || []).forEach((p) => {
    const ph = p?.paymentPhase;
    if (ph) set.add(String(ph));
  });
  return Array.from(set).sort();
}

function buildDropdownSections(regionCounts) {
  const sections = [];
  for (const [secName, prefs] of REGION_GROUPS) {
    const items = [];
    for (const pref of prefs) {
      if (regionCounts.has(pref)) {
        items.push({ key: pref, label: pref, ...regionCounts.get(pref) });
      }
    }
    if (items.length > 0) sections.push({ name: secName, items });
  }
  const overseas = [];
  for (const [key, data] of regionCounts) {
    if (key.startsWith(OVERSEAS_PREFIX)) {
      overseas.push({ key, label: key, ...data });
    }
  }
  if (overseas.length > 0)
    sections.push({ name: "海外", items: overseas });
  const handled = new Set([
    ...JP_PREFS,
    ...overseas.map((o) => o.key),
    MUHOUMATSU_KEY,
  ]);
  const others = [];
  for (const [key, data] of regionCounts) {
    if (!handled.has(key)) {
      others.push({ key, label: key, ...data });
    }
  }
  if (others.length > 0)
    sections.push({ name: "その他", items: others });
  // 無法松（締切後駆け込み組）。都道府県横断のオーバーレイなので最下部に置く。
  if (regionCounts.has(MUHOUMATSU_KEY)) {
    sections.push({
      name: "無法松（締切後）",
      items: [
        {
          key: MUHOUMATSU_KEY,
          label: MUHOUMATSU_KEY,
          ...regionCounts.get(MUHOUMATSU_KEY),
        },
      ],
    });
  }
  return sections;
}

function projectUser(id, data) {
  return {
    id,
    name: data.name || data.registrationName || "",
    avatar: data.avatar || "",
    prefecture: data.prefecture || "",
    city: data.city || "",
    usState: data.usState || "",
    country: data.country || "",
    groups2025SecondHalf: data.groups2025SecondHalf || "",
    sextype: data.sextype || "",
    bloodtype: data.bloodtype || "",
    createdAtMs:
      typeof data.createdAt?.toMillis === "function"
        ? data.createdAt.toMillis()
        : null,
    payments: Array.isArray(data.payments)
      ? data.payments.map((p) => ({ paymentPhase: p?.paymentPhase || "" }))
      : [],
    groupActivity2026: data.groupActivity2026
      ? {
          response: data.groupActivity2026.response || null,
          groupId: data.groupActivity2026.groupId || null,
        }
      : null,
  };
}

// User オブジェクト → グループドキュメントに格納する member 形式
function toMemberInfo(u) {
  return {
    uid: u.id,
    name: u.name || "",
    avatar: u.avatar || "",
    prefecture: u.prefecture || "",
    city: u.city || "",
    usState: u.usState || "",
    country: u.country || "",
    groups2025SecondHalf: u.groups2025SecondHalf || "",
    sextype: u.sextype || "",
    bloodtype: u.bloodtype || "",
    createdAtMs: u.createdAtMs || null,
    paymentPhases: mmmPhases(u.payments),
  };
}

// member オブジェクトを UserRow が期待する user 形式へ
function memberToUserShape(m) {
  return {
    id: m.uid,
    name: m.name || "",
    avatar: m.avatar || "",
    prefecture: m.prefecture || "",
    city: m.city || "",
    usState: m.usState || "",
    country: m.country || "",
    groups2025SecondHalf: m.groups2025SecondHalf || "",
    sextype: m.sextype || "",
    bloodtype: m.bloodtype || "",
    createdAtMs: m.createdAtMs || null,
    payments: (m.paymentPhases || []).map((ph) => ({ paymentPhase: ph })),
  };
}

// "2024/03/14 より参加" のような表記。createdAtMs が無いときは空文字
function formatJoinedAt(ms) {
  if (!ms) return "";
  const d = new Date(ms);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}/${mo}/${da} より参加`;
}

// ──────────────────────────────────────────────────────────────
// 自動振り分け仮提案ロジック（厳密に 5 or 6 人）
// ──────────────────────────────────────────────────────────────

// N 人を 6 人と 5 人の組み合わせで分けたときの最適解。
// 残り（leftover）を最小化、同点なら 6 人組を多くする方を優先。
function calcAutoGroupSizes(n) {
  if (n < 5) return { sixes: 0, fives: 0, leftover: n };
  let best = { sixes: 0, fives: 0, leftover: n };
  for (let a = Math.floor(n / 6); a >= 0; a--) {
    const rem = n - 6 * a;
    const b = Math.floor(rem / 5);
    const leftover = rem - 5 * b;
    if (
      leftover < best.leftover ||
      (leftover === best.leftover && a > best.sixes)
    ) {
      best = { sixes: a, fives: b, leftover };
    }
    if (leftover === 0) break;
  }
  return best;
}

function shuffleInPlace(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// 全グループにまたがる「city 分断」の合計件数。
// city が複数グループに散らばっていたら、その「余分なグループ数」だけ加算。
function totalCitySplits(groups) {
  const cityToGroupSet = new Map();
  groups.forEach((g, gi) => {
    g.forEach((u) => {
      const c = u.city || "";
      if (!c) return;
      if (!cityToGroupSet.has(c)) cityToGroupSet.set(c, new Set());
      cityToGroupSet.get(c).add(gi);
    });
  });
  let total = 0;
  cityToGroupSet.forEach((set) => {
    if (set.size > 1) total += set.size - 1;
  });
  return total;
}

// 重み付き総合スコア。優先順位：past2025 > city > sextype。
//   past2025 違反 1 件 = 10000
//   city 分断 1 件   = 100
//   sextype 違反 1 件 = 1
function weightedTotalScore(groups, options) {
  let s = 0;
  for (const g of groups) {
    if (options.past2025Diverse) {
      const counts = new Map();
      g.forEach((u) => {
        const k = u.groups2025SecondHalf || "";
        if (!k) return;
        counts.set(k, (counts.get(k) || 0) + 1);
      });
      counts.forEach((c) => {
        if (c > 1) s += 10000 * ((c * (c - 1)) / 2);
      });
    }
    if (options.sextypeBloodDiverse) {
      let m = 0;
      let f = 0;
      g.forEach((u) => {
        if (u.sextype === "男性") m += 1;
        else if (u.sextype === "女性") f += 1;
      });
      s += Math.abs(m - f);
      const bloodCounts = new Map();
      g.forEach((u) => {
        const k = u.bloodtype || "__none__";
        bloodCounts.set(k, (bloodCounts.get(k) || 0) + 1);
      });
      bloodCounts.forEach((c) => {
        if (c > 2) s += c - 2;
      });
    }
  }
  if (options.sameCityCluster) {
    s += 100 * totalCitySplits(groups);
  }
  return s;
}

// 2点間のヒュベニ距離（km）— おおよそでよい
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// 同じ市区町村のユーザーが同じグループに入るのを最優先にしたクラスタリング。
//   1. ユーザーを市区町村ごとに集約
//   2. 市区町村を nearest-neighbor tour で並べる（北端スタート → 最も近い未訪問市を順次）
//   3. tour 順にユーザーを並べ、グループサイズで順次切る
//   → 同じ市の人は連続して並ぶので、グループ境界以外では絶対に同じグループになる。
//   → 市の間は地理的に近い順なので、境界またぎも近所同士になる。
function geoClusterUsers(users, groupSizes, citiesByPref, prefecture) {
  const cityList = citiesByPref?.[prefecture] || [];
  const cityCoordMap = new Map();
  cityList.forEach((c) => {
    if (typeof c?.lat === "number" && typeof c?.lng === "number") {
      cityCoordMap.set(c.name, c);
    }
  });

  // 1. ユーザーを市区町村ごとに集約。座標が引けないものは noGeo へ。
  const byCity = new Map(); // cityName -> { lat, lng, users: [] }
  const noGeo = [];
  for (const u of users) {
    const cname = u.city || "";
    const c = cityCoordMap.get(cname);
    if (!c) {
      noGeo.push(u);
      continue;
    }
    if (!byCity.has(cname)) {
      byCity.set(cname, { lat: c.lat, lng: c.lng, users: [] });
    }
    byCity.get(cname).users.push(u);
  }

  const cities = Array.from(byCity.entries()).map(([name, c]) => ({
    name,
    lat: c.lat,
    lng: c.lng,
    users: c.users,
  }));

  let ordered = [];

  if (cities.length > 0) {
    // 2. Nearest-neighbor tour：北端の市から開始、最も近い未訪問市を順次たどる
    const tour = [];
    const visited = new Set();
    let current = cities.reduce(
      (best, c) => (c.lat > best.lat ? c : best),
      cities[0]
    );
    tour.push(current);
    visited.add(current.name);
    while (tour.length < cities.length) {
      let next = null;
      let bestDist = Infinity;
      for (const c of cities) {
        if (visited.has(c.name)) continue;
        const d = haversineKm(current.lat, current.lng, c.lat, c.lng);
        if (d < bestDist) {
          bestDist = d;
          next = c;
        }
      }
      if (!next) break;
      tour.push(next);
      visited.add(next.name);
      current = next;
    }
    // 3. tour 順にユーザーを並べる
    for (const c of tour) {
      for (const u of c.users) ordered.push(u);
    }
  }

  // 4. グループサイズで順次切る
  const groups = [];
  let idx = 0;
  for (const size of groupSizes) {
    groups.push(ordered.slice(idx, idx + size));
    idx += size;
  }
  const leftoverOrdered = ordered.slice(idx);

  // 5. noGeo を空き枠に詰める
  const leftover = [];
  const placeIntoSlot = (u) => {
    for (let i = 0; i < groups.length; i++) {
      if (groups[i].length < groupSizes[i]) {
        groups[i].push(u);
        return true;
      }
    }
    return false;
  };
  noGeo.forEach((u) => {
    if (!placeIntoSlot(u)) leftover.push(u);
  });
  leftoverOrdered.forEach((u) => leftover.push(u));

  return { groups, leftover };
}

// マイクロタスクへの yield（UI スレッドに描画機会を渡す）
function yieldToUi() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

async function autoAllocateGroups(
  users,
  options,
  citiesByPref,
  prefecture,
  onProgress
) {
  const startedAt = Date.now();
  const report = (phase, percent, message) => {
    if (typeof onProgress === "function") {
      const elapsedMs = Date.now() - startedAt;
      const etaMs =
        percent > 1 ? Math.round((elapsedMs / percent) * (100 - percent)) : 0;
      onProgress({ phase, percent, message, elapsedMs, etaMs });
    }
  };

  report("init", 1, "初期化中…");
  await yieldToUi();

  const n = users.length;
  const sizes = calcAutoGroupSizes(n);
  const groupSizes = [
    ...Array(sizes.sixes).fill(6),
    ...Array(sizes.fives).fill(5),
  ];
  if (groupSizes.length === 0) {
    report("done", 100, "完了");
    return { groups: [], leftover: users.slice(), sizes };
  }

  let groups;
  let leftover;

  if (options.sameCityCluster && citiesByPref && prefecture) {
    report("cluster", 5, "地理クラスタリング中…");
    await yieldToUi();
    const result = geoClusterUsers(
      users,
      groupSizes,
      citiesByPref,
      prefecture
    );
    groups = result.groups;
    leftover = result.leftover;
  } else {
    report("shuffle", 5, "ランダム配置中…");
    await yieldToUi();
    const pool = shuffleInPlace(users.slice());
    const totalSlots = groupSizes.reduce((s, x) => s + x, 0);
    const assigned = pool.slice(0, totalSlots);
    leftover = pool.slice(totalSlots);
    groups = [];
    let idx = 0;
    groupSizes.forEach((size) => {
      groups.push(assigned.slice(idx, idx + size));
      idx += size;
    });
  }

  // 重み付きスコアで swap 最適化。
  // 全グループの再スキャンを避けるため、属性ごとの index を保持して
  // swap で変わる箇所だけ差分計算する。
  if (options.past2025Diverse || options.sextypeBloodDiverse) {
    const K = groups.length;
    const past2025Counts = Array.from({ length: K }, () => new Map());
    const sexCounts = Array.from({ length: K }, () => ({ m: 0, f: 0 }));
    const bloodCounts = Array.from({ length: K }, () => new Map());
    const cityToGroupCounts = new Map(); // city -> Map<groupIdx, count>

    const addUser = (u, gi) => {
      const p = u.groups2025SecondHalf || "";
      if (p) past2025Counts[gi].set(p, (past2025Counts[gi].get(p) || 0) + 1);
      if (u.sextype === "男性") sexCounts[gi].m += 1;
      else if (u.sextype === "女性") sexCounts[gi].f += 1;
      const b = u.bloodtype || "";
      if (b) bloodCounts[gi].set(b, (bloodCounts[gi].get(b) || 0) + 1);
      const c = u.city || "";
      if (c) {
        let m = cityToGroupCounts.get(c);
        if (!m) {
          m = new Map();
          cityToGroupCounts.set(c, m);
        }
        m.set(gi, (m.get(gi) || 0) + 1);
      }
    };
    const removeUser = (u, gi) => {
      const p = u.groups2025SecondHalf || "";
      if (p) {
        const v = (past2025Counts[gi].get(p) || 0) - 1;
        if (v <= 0) past2025Counts[gi].delete(p);
        else past2025Counts[gi].set(p, v);
      }
      if (u.sextype === "男性") sexCounts[gi].m -= 1;
      else if (u.sextype === "女性") sexCounts[gi].f -= 1;
      const b = u.bloodtype || "";
      if (b) {
        const v = (bloodCounts[gi].get(b) || 0) - 1;
        if (v <= 0) bloodCounts[gi].delete(b);
        else bloodCounts[gi].set(b, v);
      }
      const c = u.city || "";
      if (c) {
        const m = cityToGroupCounts.get(c);
        if (m) {
          const v = (m.get(gi) || 0) - 1;
          if (v <= 0) m.delete(gi);
          else m.set(gi, v);
          if (m.size === 0) cityToGroupCounts.delete(c);
        }
      }
    };

    const past2025PenaltyFor = (gi) => {
      if (!options.past2025Diverse) return 0;
      let s = 0;
      past2025Counts[gi].forEach((c) => {
        if (c > 1) s += (c * (c - 1)) / 2;
      });
      return s * 10000;
    };
    const sexPenaltyFor = (gi) => {
      if (!options.sextypeBloodDiverse) return 0;
      let s = Math.abs(sexCounts[gi].m - sexCounts[gi].f);
      bloodCounts[gi].forEach((c) => {
        if (c > 2) s += c - 2;
      });
      return s;
    };
    const cityPenaltyFor = (city) => {
      if (!options.sameCityCluster) return 0;
      if (!city) return 0;
      const m = cityToGroupCounts.get(city);
      if (!m) return 0;
      return m.size > 1 ? (m.size - 1) * 100 : 0;
    };

    // 初期 index 構築
    groups.forEach((g, gi) => {
      g.forEach((u) => addUser(u, gi));
    });

    const MAX_ITER = 50;
    const perIterSpan = 88 / MAX_ITER;
    for (let iter = 0; iter < MAX_ITER; iter++) {
      let improved = false;
      const baselinePercent = 10 + (iter / MAX_ITER) * 88;
      report(
        "optimize",
        baselinePercent,
        `多様性最適化中…（${iter + 1} / ${MAX_ITER} 周目）`
      );
      await yieldToUi();

      const giMax = K - 1;
      for (let gi = 0; gi < giMax; gi++) {
        for (let gj = gi + 1; gj < K; gj++) {
          for (let i = 0; i < groups[gi].length; i++) {
            for (let j = 0; j < groups[gj].length; j++) {
              const a = groups[gi][i];
              const b = groups[gj][j];

              const beforeP = past2025PenaltyFor(gi) + past2025PenaltyFor(gj);
              const beforeS = sexPenaltyFor(gi) + sexPenaltyFor(gj);
              const beforeC =
                cityPenaltyFor(a.city) +
                (a.city === b.city ? 0 : cityPenaltyFor(b.city));
              const before = beforeP + beforeS + beforeC;

              // 仮 swap
              removeUser(a, gi);
              removeUser(b, gj);
              addUser(b, gi);
              addUser(a, gj);
              groups[gi][i] = b;
              groups[gj][j] = a;

              const afterP = past2025PenaltyFor(gi) + past2025PenaltyFor(gj);
              const afterS = sexPenaltyFor(gi) + sexPenaltyFor(gj);
              const afterC =
                cityPenaltyFor(a.city) +
                (a.city === b.city ? 0 : cityPenaltyFor(b.city));
              const after = afterP + afterS + afterC;

              if (after < before) {
                improved = true;
              } else {
                // revert
                removeUser(b, gi);
                removeUser(a, gj);
                addUser(a, gi);
                addUser(b, gj);
                groups[gi][i] = a;
                groups[gj][j] = b;
              }
            }
          }
        }
        if (gi % 5 === 4 || gi === giMax - 1) {
          const subPercent =
            baselinePercent + ((gi + 1) / giMax) * perIterSpan;
          report(
            "optimize",
            subPercent,
            `多様性最適化中…（${iter + 1} / ${MAX_ITER} 周目・${gi + 1} / ${giMax}）`
          );
          await yieldToUi();
        }
      }
      if (!improved) break;
    }
  }

  report("done", 100, "完了");
  return { groups, leftover, sizes };
}

// ──────────────────────────────────────────────────────────────
// main
// ──────────────────────────────────────────────────────────────

export default function AdminGroupAllocation2026() {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const surface = theme.surfaceBg ?? "#FFFFFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [aggregate, setAggregate] = useState({
    regions: {},
    totalJoin: 0,
    totalDecline: 0,
    updatedAt: null,
  });
  const [aggregateMissing, setAggregateMissing] = useState(false);
  const [groups, setGroups] = useState([]);

  // 地域別の lazy フェッチ結果。 { [regionKey]: User[] }
  const [regionUsersCache, setRegionUsersCache] = useState({});
  // 地域別のフェッチ中フラグ
  const [regionLoading, setRegionLoading] = useState({});

  const [selectedUnassigned, setSelectedUnassigned] = useState(() => new Set());
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [renameDraft, setRenameDraft] = useState({});
  const [activeGroupId, setActiveGroupId] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [mapDialog, setMapDialog] = useState(null); // { mode, regionKey, users }
  const [snackbarMsg, setSnackbarMsg] = useState("");

  // 2025後期グループ id → name のマップ。市区町村ソート時の各ユーザー表示用。
  const [pastGroupNamesById, setPastGroupNamesById] = useState({});

  // 自動振り分け仮提案ダイアログ
  // { prefecture: string, users: User[] } または null
  const [autoAllocDialog, setAutoAllocDialog] = useState(null);

  // ── load aggregate ──
  const loadAggregate = async () => {
    setSyncing(true);
    try {
      const snap = await getDoc(
        doc(db, "groupActivity2026Aggregates", "regions")
      );
      if (!snap.exists()) {
        setAggregate({
          regions: {},
          totalJoin: 0,
          totalDecline: 0,
          updatedAt: null,
        });
        setAggregateMissing(true);
      } else {
        const d = snap.data() || {};
        setAggregate({
          regions: d.regions || {},
          totalJoin: d.totalJoin || 0,
          totalDecline: d.totalDecline || 0,
          updatedAt:
            typeof d.updatedAt?.toDate === "function"
              ? d.updatedAt.toDate()
              : null,
        });
        setAggregateMissing(false);
      }
    } catch (e) {
      console.error("aggregate load error:", e);
      setErrorMsg("集計ドキュメントの取得に失敗しました。");
    } finally {
      setSyncing(false);
    }
  };

  // ── load groups ──
  const loadGroups = async () => {
    setSyncing(true);
    try {
      const snap = await getDocs(collection(db, "groupActivity2026Groups"));
      const acc = [];
      snap.forEach((d) => {
        const data = d.data() || {};
        const rawMembers = Array.isArray(data.members) ? data.members : [];
        // 旧形式（string uid 配列）は読み飛ばさず、{uid} のみ持つ最小 member に正規化
        const members = rawMembers.map((m) =>
          typeof m === "string"
            ? { uid: m, name: "", avatar: "", paymentPhases: [] }
            : m
        );
        acc.push({
          id: d.id,
          cohort: data.cohort || "normal",
          region: data.region || "",
          name: data.name || "",
          status: data.status || "draft",
          members,
          createdAtMs:
            typeof data.createdAt?.toMillis === "function"
              ? data.createdAt.toMillis()
              : 0,
        });
      });
      setGroups(acc);
    } catch (e) {
      console.error("groups load error:", e);
      setErrorMsg("グループの取得に失敗しました。");
    } finally {
      setSyncing(false);
    }
  };

  // ── lazy load users for a region ──
  // 海外地域は usState/country 単位で集計ドキュメントに入っているが、
  // クエリは prefecture 単位（海外（アメリカ）/海外（その他））でまとめて取得し、
  // 取得結果を regionKey() でバケット分割してキャッシュする。
  // 「無法松」は都道府県横断のコホートなので、prefecture ではなく
  // respondedAt の範囲条件でクエリを切り替える。
  const loadRegionUsers = async (regionKeyStr) => {
    if (!regionKeyStr) return;
    if (regionUsersCache[regionKeyStr]) return;
    if (regionLoading[regionKeyStr]) return;

    setRegionLoading((prev) => ({ ...prev, [regionKeyStr]: true }));
    try {
      let q;
      if (regionKeyStr === MUHOUMATSU_KEY) {
        q = query(
          collection(db, "users"),
          where("groupActivity2026.response", "==", "join"),
          where(
            "groupActivity2026.respondedAt",
            ">=",
            Timestamp.fromDate(MUHOUMATSU_CUTOFF_DATE)
          )
        );
      } else {
        // 海外は親キー単位（"海外（アメリカ）" / "海外（その他）"）でクエリ。
        q = query(
          collection(db, "users"),
          where("groupActivity2026.response", "==", "join"),
          where("prefecture", "==", regionKeyStr)
        );
      }
      const snap = await getDocs(q);
      const all = [];
      snap.forEach((d) => {
        const data = d.data() || {};
        if (!is4kiPaidUser(data)) return;
        all.push(projectUser(d.id, data));
      });

      setRegionUsersCache((prev) => ({ ...prev, [regionKeyStr]: all }));
    } catch (e) {
      console.error("region users load error:", e);
      // Firestore の複合インデックスが未作成のときはここに来る。
      // エラーメッセージにコンソールのURLが入るので、それを画面にも出す。
      setErrorMsg(
        `「${regionKeyStr}」のユーザー取得に失敗しました。Firestore のインデックスが未作成の可能性があります。コンソールログを確認してください。`
      );
    } finally {
      setRegionLoading((prev) => {
        const next = { ...prev };
        delete next[regionKeyStr];
        return next;
      });
    }
  };

  // ── backfill ──
  const runBackfill = async () => {
    if (
      !window.confirm(
        "集計ドキュメントを再構築します（users 全件をスキャン）。よろしいですか？"
      )
    )
      return;
    setSyncing(true);
    setErrorMsg("");
    setInfoMsg("");
    try {
      const fn = httpsCallable(
        functions,
        "backfillGroupActivity2026Aggregate"
      );
      const res = await fn();
      const { scanned, totalJoin, totalDecline, regionCount } = res.data || {};
      setInfoMsg(
        `集計を再構築しました：scanned=${scanned}, join=${totalJoin}, decline=${totalDecline}, regions=${regionCount}`
      );
      await loadAggregate();
    } catch (e) {
      console.error("backfill error:", e);
      setErrorMsg(`集計の再構築に失敗しました：${e.message || e}`);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([loadAggregate(), loadGroups()]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 起動時に「参加」回答者を全員一括取得して、都道府県ごとにバケット分けする。
  //
  // 目的：
  //   - カウントを uid ベースで正確に計算する。
  //     aggregate の join は現住所ベース、group members の prefecture は
  //     グループ確定時のスナップショット。この間で「編成後に引越した」
  //     ユーザーがドリフトして「幽霊未編入」を生む問題を解消する。
  //   - 地域選択時のラグを削減（既にキャッシュ済み）。
  //   - 無法松バケットもこの一括結果から派生させる。
  //
  // ~数千件の users 取得なので初期表示に数秒かかるが、
  // その間 aggregate ベースの近似値が dropdown に表示される。
  useEffect(() => {
    (async () => {
      try {
        const q = query(
          collection(db, "users"),
          where("groupActivity2026.response", "==", "join")
        );
        const snap = await getDocs(q);
        const buckets = {};
        const muhoumatsu = [];
        const cutoffMs = MUHOUMATSU_CUTOFF_DATE.getTime();
        snap.forEach((d) => {
          const data = d.data() || {};
          if (!is4kiPaidUser(data)) return;
          const projected = projectUser(d.id, data);
          const key = (data.prefecture || "").trim() || "未登録";
          if (!buckets[key]) buckets[key] = [];
          buckets[key].push(projected);
          const respondedAt = data.groupActivity2026?.respondedAt;
          const respondedAtMs =
            typeof respondedAt?.toMillis === "function"
              ? respondedAt.toMillis()
              : 0;
          if (respondedAtMs >= cutoffMs) {
            muhoumatsu.push(projected);
          }
        });
        buckets[MUHOUMATSU_KEY] = muhoumatsu;
        setRegionUsersCache((prev) => ({ ...prev, ...buckets }));
      } catch (e) {
        console.error("全参加者取得エラー:", e);
        // fallback: 地域選択時の loadRegionUsers に委ねる（aggregate 表示のまま）
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2025後期グループ一覧（id → name）を一度だけ取得
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "groups2025halfSecond"));
        const map = {};
        snap.forEach((d) => {
          const data = d.data() || {};
          map[d.id] = data.name || d.id;
        });
        setPastGroupNamesById(map);
      } catch (e) {
        console.error("2025後期グループ name load error:", e);
      }
    })();
  }, []);

  // ── derived ──

  // groupCount per region（編成済み人数）
  // グループドキュメントの region フィールドではなく、メンバー個別の
  // prefecture/usState/country から regionKey を引いて集計する。
  // （新規グループは region="未指定" で作られるが、編成済みのカウントは
  //  実メンバーがどの都道府県から来たかで集計したい）
  const regionAssigned = useMemo(() => {
    const m = new Map();
    groups.forEach((g) => {
      g.members.forEach((member) => {
        const key = regionKey(member);
        m.set(key, (m.get(key) || 0) + 1);
      });
    });
    return m;
  }, [groups]);

  const assignedUids = useMemo(() => {
    const s = new Set();
    groups.forEach((g) => g.members.forEach((m) => s.add(m.uid)));
    return s;
  }, [groups]);

  // ドロップダウン用のカウントマップ：{join, assigned, unassigned}
  // 海外（アメリカ）/{state}, 海外（その他）/{country} は親キーへロールアップ。
  const regionCounts = useMemo(() => {
    const rollup = (key) => {
      if (key.startsWith("海外（アメリカ）/")) return "海外（アメリカ）";
      if (key.startsWith("海外（その他）/")) return "海外（その他）";
      return key;
    };
    const m = new Map();
    const ensure = (k) => {
      if (!m.has(k)) m.set(k, { join: 0, assigned: 0, unassigned: 0 });
      return m.get(k);
    };
    Object.entries(aggregate.regions || {}).forEach(([key, c]) => {
      const pk = rollup(key);
      ensure(pk).join += c.join || 0;
    });
    regionAssigned.forEach((cnt, region) => {
      const pk = rollup(region);
      ensure(pk).assigned += cnt;
    });
    for (const v of m.values()) {
      v.unassigned = Math.max(0, v.join - v.assigned);
    }
    // フェッチ済み地域（regionUsersCache に実データがある）は
    // uid ベースで正確に上書きする。
    //
    // aggregate の join は「現在の prefecture」ベース、
    // regionAssigned の assigned は「グループ member スナップショットの prefecture」ベース
    // のため、編成後に引越したユーザーがドリフトして「幽霊未編入」を生む問題を解消する。
    // 無法松（都道府県横断コホート）も同じロジックでカバーされる。
    Object.entries(regionUsersCache).forEach(([key, users]) => {
      if (!Array.isArray(users)) return;
      const join = users.length;
      let assigned = 0;
      users.forEach((u) => {
        if (assignedUids.has(u.id)) assigned += 1;
      });
      m.set(key, {
        join,
        assigned,
        unassigned: Math.max(0, join - assigned),
      });
    });
    return m;
  }, [aggregate, regionAssigned, regionUsersCache, assignedUids]);

  const visibleGroups = useMemo(
    () =>
      [...groups].sort(
        (a, b) =>
          a.region.localeCompare(b.region, "ja") ||
          a.name.localeCompare(b.name, "ja")
      ),
    [groups]
  );

  // uid → 所属グループ情報（編成済みユーザーを左リストでグレー表示する用）
  const assignedToGroup = useMemo(() => {
    const m = new Map();
    groups.forEach((g) =>
      g.members.forEach((member) =>
        m.set(member.uid, { groupId: g.id, groupName: g.name })
      )
    );
    return m;
  }, [groups]);

  const totals = useMemo(() => {
    const totalJoin = aggregate.totalJoin || 0;
    const totalAssigned = Array.from(regionAssigned.values()).reduce(
      (s, v) => s + v,
      0
    );
    return {
      totalJoin,
      totalAssigned,
      totalUnassigned: Math.max(0, totalJoin - totalAssigned),
    };
  }, [aggregate, regionAssigned]);

  // ── selection helpers ──
  const toggleSelect = useCallback((uid) => {
    const { activeGroupId, selectedUnassigned } = stateRef.current;
    // 仮追加先未選択かつ「追加方向」のクリックなら案内のみ
    if (!activeGroupId && !selectedUnassigned.has(uid)) {
      setSnackbarMsg("先に右パネルで追加先のグループを選んでください。");
      return;
    }
    setSelectedUnassigned((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }, []);
  const selectAllInRegion = useCallback((uids) => {
    const { activeGroupId } = stateRef.current;
    if (!activeGroupId) {
      setSnackbarMsg("先に右パネルで追加先のグループを選んでください。");
      return;
    }
    setSelectedUnassigned((prev) => {
      const next = new Set(prev);
      uids.forEach((u) => next.add(u));
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedUnassigned(new Set()), []);

  const toggleGroupOpen = useCallback((gid) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  }, []);

  // setActiveGroupId は state setter なので元から安定。トグル動作を内包する版を用意。
  const toggleActiveGroupId = useCallback((gid) => {
    setActiveGroupId((prev) => (prev === gid ? "" : gid));
  }, []);

  // setRenameDraft の per-card 版（stable）
  const setRenameDraftFor = useCallback((gid, val) => {
    setRenameDraft((prev) => ({ ...prev, [gid]: val }));
  }, []);

  // ── 選択中ユーザーの完全情報を引く（regionUsersCache から） ──
  const usersInCache = useMemo(() => {
    const m = new Map();
    Object.values(regionUsersCache).forEach((list) =>
      list.forEach((u) => m.set(u.id, u))
    );
    return m;
  }, [regionUsersCache]);

  // 800+ グループでの再レンダリング軽減用：state スナップショットを ref で渡し、
  // ハンドラを useCallback([]) で安定化できるようにする。
  const stateRef = useRef({});
  stateRef.current = {
    groups,
    activeGroupId,
    selectedUnassigned,
    usersInCache,
    renameDraft,
  };

  // ── actions ──

  const removeMemberFromGroup = useCallback(async (gid, uid) => {
    const { groups } = stateRef.current;
    const g = groups.find((x) => x.id === gid);
    if (!g) return;
    if (
      !window.confirm(
        `このメンバーを「${g.name}」から外して未割り当てに戻します。よろしいですか？`
      )
    )
      return;
    setSyncing(true);
    try {
      const newMembers = g.members.filter((m) => m.uid !== uid);
      await updateDoc(doc(db, "groupActivity2026Groups", gid), {
        members: newMembers,
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "users", uid), {
        "groupActivity2026.groupId": null,
      });
      setGroups((prev) =>
        prev.map((x) => (x.id === gid ? { ...x, members: newMembers } : x))
      );
    } catch (e) {
      console.error("remove member error:", e);
      setErrorMsg("メンバー外しに失敗しました。");
    } finally {
      setSyncing(false);
    }
  }, []);

  const moveMemberToGroup = useCallback(async (fromGid, uid, toGid) => {
    if (!toGid || fromGid === toGid) return;
    const { groups } = stateRef.current;
    const fromG = groups.find((x) => x.id === fromGid);
    const toG = groups.find((x) => x.id === toGid);
    if (!fromG || !toG) return;
    const memberObj = fromG.members.find((m) => m.uid === uid);
    if (!memberObj) return;
    if (
      toG.members.length + 1 > DEFAULT_GROUP_MAX &&
      !window.confirm(
        `「${toG.name}」が ${toG.members.length + 1} 人になります（推奨上限 ${DEFAULT_GROUP_MAX} 人）。よろしいですか？`
      )
    )
      return;
    setSyncing(true);
    try {
      const fromMembers = fromG.members.filter((m) => m.uid !== uid);
      const toMembers = [...toG.members, memberObj];
      const batch = writeBatch(db);
      batch.update(doc(db, "groupActivity2026Groups", fromGid), {
        members: fromMembers,
        updatedAt: serverTimestamp(),
      });
      batch.update(doc(db, "groupActivity2026Groups", toGid), {
        members: toMembers,
        updatedAt: serverTimestamp(),
      });
      batch.update(doc(db, "users", uid), {
        "groupActivity2026.groupId": toGid,
      });
      await batch.commit();
      setGroups((prev) =>
        prev.map((x) =>
          x.id === fromGid
            ? { ...x, members: fromMembers }
            : x.id === toGid
            ? { ...x, members: toMembers }
            : x
        )
      );
    } catch (e) {
      console.error("move member error:", e);
      setErrorMsg("メンバー移動に失敗しました。");
    } finally {
      setSyncing(false);
    }
  }, []);

  const renameGroup = useCallback(async (gid) => {
    const { renameDraft } = stateRef.current;
    const newName = (renameDraft[gid] || "").trim();
    if (!newName) return;
    setSyncing(true);
    try {
      await updateDoc(doc(db, "groupActivity2026Groups", gid), {
        name: newName,
        updatedAt: serverTimestamp(),
      });
      setGroups((prev) =>
        prev.map((x) => (x.id === gid ? { ...x, name: newName } : x))
      );
      setRenameDraft((prev) => {
        const next = { ...prev };
        delete next[gid];
        return next;
      });
    } catch (e) {
      console.error("rename error:", e);
      setErrorMsg("名前変更に失敗しました。");
    } finally {
      setSyncing(false);
    }
  }, []);

  // 確定ボタン：仮追加（左で選択中の未割り当てユーザー）を本追加し、status を finalized に。
  // 既に finalized で仮追加も無い → 差し戻し（draft へ）。
  const toggleFinalize = useCallback(async (gid) => {
    const { groups, activeGroupId, selectedUnassigned, usersInCache } =
      stateRef.current;
    const g = groups.find((x) => x.id === gid);
    if (!g) return;
    const isActive = activeGroupId === gid;
    const stagedUids = isActive ? Array.from(selectedUnassigned) : [];
    const stagedUsers = stagedUids
      .map((uid) => usersInCache.get(uid))
      .filter(Boolean);

    // 仮追加が無く、既に finalized → 差し戻し
    if (stagedUsers.length === 0 && g.status === "finalized") {
      if (!window.confirm(`「${g.name}」を下書きに差し戻します。よろしいですか？`))
        return;
      setSyncing(true);
      try {
        await updateDoc(doc(db, "groupActivity2026Groups", gid), {
          status: "draft",
          finalizedAt: null,
          updatedAt: serverTimestamp(),
        });
        setGroups((prev) =>
          prev.map((x) => (x.id === gid ? { ...x, status: "draft" } : x))
        );
      } catch (e) {
        console.error("revert finalize error:", e);
        setErrorMsg("差し戻しに失敗しました。");
      } finally {
        setSyncing(false);
      }
      return;
    }

    // 仮追加がある（または draft → finalize）：仮追加分をコミットして finalized に
    const stagedMemberInfos = stagedUsers.map(toMemberInfo);
    const newMembers = [...g.members, ...stagedMemberInfos];
    const msg =
      stagedUsers.length > 0
        ? `「${g.name}」に ${stagedUsers.length} 人を追加して確定します。よろしいですか？`
        : `「${g.name}」を確定します（差し戻し可）。よろしいですか？`;
    if (!window.confirm(msg)) return;

    setSyncing(true);
    try {
      await updateDoc(doc(db, "groupActivity2026Groups", gid), {
        members: newMembers,
        status: "finalized",
        finalizedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      if (stagedUsers.length > 0) {
        const batch = writeBatch(db);
        stagedUsers.forEach((u) =>
          batch.update(doc(db, "users", u.id), {
            "groupActivity2026.groupId": gid,
          })
        );
        await batch.commit();
      }
      setGroups((prev) =>
        prev.map((x) =>
          x.id === gid
            ? { ...x, members: newMembers, status: "finalized" }
            : x
        )
      );
      setSelectedUnassigned(new Set());
    } catch (e) {
      console.error("commit/finalize error:", e);
      setErrorMsg("確定に失敗しました。");
    } finally {
      setSyncing(false);
    }
  }, []);

  const removeGroup = useCallback(async (gid) => {
    const { groups } = stateRef.current;
    const g = groups.find((x) => x.id === gid);
    if (!g) return;
    if (
      !window.confirm(
        `「${g.name}」グループを削除し、メンバー ${g.members.length} 人を未割り当てに戻します。よろしいですか？`
      )
    )
      return;
    setSyncing(true);
    try {
      if (g.members.length > 0) {
        const batch = writeBatch(db);
        g.members.forEach((m) =>
          batch.update(doc(db, "users", m.uid), {
            "groupActivity2026.groupId": null,
          })
        );
        await batch.commit();
      }
      await deleteDoc(doc(db, "groupActivity2026Groups", gid));
      setGroups((prev) => prev.filter((x) => x.id !== gid));
    } catch (e) {
      console.error("delete group error:", e);
      setErrorMsg("削除に失敗しました。");
    } finally {
      setSyncing(false);
    }
  }, []);

  // 空のグループを複数まとめて作成
  //   text      "関東"
  //   count     5
  //   startNum  3       → "関東03", "関東04", "関東05", "関東06", "関東07"
  const createEmptyGroups = async ({ text, count, startNum }) => {
    const t = (text || "").trim();
    const c = Math.max(1, Math.min(300, Number(count) || 1));
    const s = Math.max(1, Math.min(300, Number(startNum) || 1));
    setSyncing(true);
    try {
      const refs = [];
      const newGroupObjs = [];
      const batch = writeBatch(db);
      for (let i = 0; i < c; i++) {
        const num = s + i;
        const suffix = String(num).padStart(3, "0");
        const name = `${t}${suffix}`;
        const ref = doc(collection(db, "groupActivity2026Groups"));
        batch.set(ref, {
          cohort: "normal",
          region: "未指定",
          name,
          status: "draft",
          members: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        refs.push(ref);
        newGroupObjs.push({
          id: ref.id,
          cohort: "normal",
          region: "未指定",
          name,
          status: "draft",
          members: [],
          createdAtMs: Date.now(),
        });
      }
      await batch.commit();
      setGroups((prev) => [...prev, ...newGroupObjs]);
      setOpenGroups((prev) => {
        const next = new Set(prev);
        refs.forEach((r) => next.add(r.id));
        return next;
      });
      setCreateDialogOpen(false);
    } catch (e) {
      console.error("create empty groups error:", e);
      setErrorMsg("グループ作成に失敗しました。");
    } finally {
      setSyncing(false);
    }
  };

  // 自動振り分け仮提案：複数の draft グループを一括作成し、各メンバーの
  // groupActivity2026.groupId を更新する。既存の同地域グループ名末尾の通し番号を
  // 引き継いで命名（例：2026石川県001、2026石川県002…）。
  const executeAutoAllocation = useCallback(
    async ({ prefecture, allocatedGroups }) => {
      if (!prefecture || !Array.isArray(allocatedGroups) || !allocatedGroups.length)
        return { ok: false, message: "提案が空です。" };

      const { groups: existingGroups } = stateRef.current;

      // 同じ prefecture の既存グループから次の通し番号を推測。
      // 名前末尾の数字を見るので "○○県NNN" / "2026○○県NNN" どちらでも拾える。
      let nextNum = 1;
      existingGroups
        .filter(
          (g) =>
            g.region === prefecture ||
            (g.name || "").includes(prefecture)
        )
        .forEach((g) => {
          const m = (g.name || "").match(/(\d+)$/);
          if (m) nextNum = Math.max(nextNum, parseInt(m[1], 10) + 1);
        });

      setSyncing(true);
      try {
        const batch = writeBatch(db);
        const newGroupObjs = [];
        const newOpenIds = [];

        allocatedGroups.forEach((memberList, idx) => {
          const num = nextNum + idx;
          const suffix = String(num).padStart(3, "0");
          const name = `2026${prefecture}${suffix}`;
          const ref = doc(collection(db, "groupActivity2026Groups"));
          const members = memberList.map(toMemberInfo);
          batch.set(ref, {
            cohort: "normal",
            region: prefecture,
            name,
            status: "draft",
            members,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          memberList.forEach((u) => {
            batch.update(doc(db, "users", u.id), {
              "groupActivity2026.groupId": ref.id,
            });
          });
          newGroupObjs.push({
            id: ref.id,
            cohort: "normal",
            region: prefecture,
            name,
            status: "draft",
            members,
            createdAtMs: Date.now(),
          });
          newOpenIds.push(ref.id);
        });

        await batch.commit();
        setGroups((prev) => [...prev, ...newGroupObjs]);
        setOpenGroups((prev) => {
          const next = new Set(prev);
          newOpenIds.forEach((id) => next.add(id));
          return next;
        });
        return { ok: true, count: allocatedGroups.length };
      } catch (e) {
        console.error("auto allocation error:", e);
        setErrorMsg("自動振り分けの実行に失敗しました。");
        return { ok: false, message: e.message || String(e) };
      } finally {
        setSyncing(false);
      }
    },
    []
  );

  const handleRefresh = async () => {
    setLoading(true);
    setRegionUsersCache({});
    await Promise.all([loadAggregate(), loadGroups()]);
    setLoading(false);
  };

  // ──────────── render ────────────

  // 全グループの slim 表現（移動先 Select 用）。visibleGroups が変わったときだけ再生成。
  const allGroupOptions = useMemo(
    () =>
      visibleGroups.map((g) => ({
        id: g.id,
        label: `${g.name}（${g.members.length}人）`,
      })),
    [visibleGroups]
  );

  const onCreateClick = useCallback(() => setCreateDialogOpen(true), []);
  const onCloseCreateDialog = useCallback(
    () => setCreateDialogOpen(false),
    []
  );
  const onCloseMapDialog = useCallback(() => setMapDialog(null), []);

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      {/* ヘッダー */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate("/admin")} size="small">
          <ArrowBackRoundedIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: "0.18em",
              fontSize: 11,
              opacity: 0.8,
              color: textBase,
            }}
          >
            ADMIN
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase }}
          >
            2026 グループ分け
          </Typography>
        </Box>
        <Tooltip title="集計を再構築（users 全件スキャン・管理者専用）">
          <span>
            <IconButton onClick={runBackfill} disabled={syncing}>
              <BuildRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="再読み込み">
          <span>
            <IconButton onClick={handleRefresh} disabled={syncing}>
              <RefreshRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.65)", mb: 2 }}>
        4期（MM2026）支払い済みで「参加する」と回答したユーザーを、地域別に
        {DEFAULT_GROUP_MIN}〜{DEFAULT_GROUP_MAX}人のグループに編成します。
      </Typography>

      {/* 同期状態 */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        {syncing && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <CircularProgress size={12} />
            <Typography variant="caption" sx={{ color: accent }}>
              同期中…
            </Typography>
          </Stack>
        )}
        <Chip
          size="small"
          label={`参加 ${totals.totalJoin}`}
          sx={{
            bgcolor: "#2e7d3214",
            color: "#2e7d32",
            border: "1px solid #2e7d3233",
            fontWeight: 800,
          }}
        />
        <Chip
          size="small"
          label={`編成済み ${totals.totalAssigned}`}
          sx={{
            bgcolor: `${accent}14`,
            color: accent,
            border: `1px solid ${accent}33`,
            fontWeight: 800,
          }}
        />
        <Chip
          size="small"
          label={`未編入 ${totals.totalUnassigned}`}
          sx={{
            bgcolor: "#b0880014",
            color: "#b08800",
            border: "1px solid #b0880033",
            fontWeight: 800,
          }}
        />
        {aggregate.updatedAt && (
          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.55)" }}>
            集計更新：{aggregate.updatedAt.toLocaleString("ja-JP")}
          </Typography>
        )}
      </Stack>

      {aggregateMissing && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: "#b0880014",
            border: "1px solid #b0880033",
            color: "#7a5e00",
            fontSize: 14,
          }}
        >
          集計ドキュメントが未生成です。右上の <BuildRoundedIcon
            fontSize="inherit"
            sx={{ verticalAlign: "middle" }}
          /> ボタンから「集計を再構築」を実行してください。
        </Paper>
      )}

      {infoMsg && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: "#2e7d3210",
            border: "1px solid #2e7d3233",
            color: "#1b5e20",
            fontSize: 14,
          }}
        >
          {infoMsg}
        </Paper>
      )}

      {errorMsg && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: "rgba(220,60,60,0.08)",
            border: "1px solid rgba(220,60,60,0.3)",
            color: "rgb(160,30,30)",
            fontSize: 14,
          }}
        >
          {errorMsg}
        </Paper>
      )}

      {loading ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                maxHeight: { md: "calc(100vh - 280px)" },
                overflowY: { md: "auto" },
                pr: { md: 0.5 },
              }}
            >
            <UnassignedSection
              regionCounts={regionCounts}
              regionUsersCache={regionUsersCache}
              regionLoading={regionLoading}
              assignedUids={assignedUids}
              assignedToGroup={assignedToGroup}
              loadRegionUsers={loadRegionUsers}
              selectAllInRegion={selectAllInRegion}
              selectedUnassigned={selectedUnassigned}
              toggleSelect={toggleSelect}
              clearSelection={clearSelection}
              activeGroupId={activeGroupId}
              activeGroup={
                activeGroupId
                  ? groups.find((g) => g.id === activeGroupId)
                  : null
              }
              onOpenMap={setMapDialog}
              onOpenAutoAlloc={setAutoAllocDialog}
              syncing={syncing}
              surface={surface}
              border={border}
              textBase={textBase}
              accent={accent}
              pastGroupNamesById={pastGroupNamesById}
            />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                maxHeight: { md: "calc(100vh - 280px)" },
                overflowY: { md: "auto" },
                pr: { md: 0.5 },
              }}
            >
            <GroupsSection
              visibleGroups={visibleGroups}
              openGroups={openGroups}
              toggleGroupOpen={toggleGroupOpen}
              removeMemberFromGroup={removeMemberFromGroup}
              moveMemberToGroup={moveMemberToGroup}
              allGroupOptions={allGroupOptions}
              renameDraft={renameDraft}
              setRenameDraftFor={setRenameDraftFor}
              renameGroup={renameGroup}
              toggleFinalize={toggleFinalize}
              removeGroup={removeGroup}
              activeGroupId={activeGroupId}
              toggleActiveGroupId={toggleActiveGroupId}
              selectedUnassigned={selectedUnassigned}
              usersInCache={usersInCache}
              onCreateClick={onCreateClick}
              syncing={syncing}
              surface={surface}
              border={border}
              textBase={textBase}
              accent={accent}
            />
            </Box>
          </Grid>
        </Grid>
      )}

      <CreateGroupsDialog
        open={createDialogOpen}
        onClose={onCloseCreateDialog}
        onSubmit={createEmptyGroups}
        syncing={syncing}
        accent={accent}
      />

      <MapDialog
        open={!!mapDialog}
        onClose={onCloseMapDialog}
        mode={mapDialog?.mode}
        regionKey={mapDialog?.regionKey}
        users={mapDialog?.users || []}
        assignedToGroup={assignedToGroup}
        accent={accent}
        titleSuffix={mapDialog?.titleSuffix}
      />

      <AutoAllocationDialog
        open={!!autoAllocDialog}
        onClose={() => setAutoAllocDialog(null)}
        prefecture={autoAllocDialog?.prefecture || ""}
        users={autoAllocDialog?.users || []}
        pastGroupNamesById={pastGroupNamesById}
        onExecute={executeAutoAllocation}
        onOpenMap={setMapDialog}
        syncing={syncing}
        accent={accent}
        textBase={textBase}
        border={border}
      />

      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={3000}
        onClose={() => setSnackbarMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setSnackbarMsg("")}
          sx={{ fontWeight: 700 }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────
// Create groups dialog
// ──────────────────────────────────────────────────────────────

function CreateGroupsDialog({ open, onClose, onSubmit, syncing, accent }) {
  const [text, setText] = useState("");
  const [count, setCount] = useState(1);
  const [startNum, setStartNum] = useState(1);

  useEffect(() => {
    if (open) {
      setText("");
      setCount(1);
      setStartNum(1);
    }
  }, [open]);

  const previewNames = useMemo(() => {
    const t = text.trim();
    const c = Math.max(1, Math.min(300, Number(count) || 1));
    const s = Math.max(1, Math.min(300, Number(startNum) || 1));
    const names = [];
    for (let i = 0; i < c && i < 8; i++) {
      const num = s + i;
      names.push(`${t}${String(num).padStart(3, "0")}`);
    }
    if (c > 8) names.push("…");
    return names;
  }, [text, count, startNum]);

  const handleSubmit = () => {
    if (!text.trim()) return;
    onSubmit({ text, count, startNum });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>新規グループを作成</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="グループ名のテキスト"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="例：関東"
            size="small"
            autoFocus
            fullWidth
          />
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="個数"
              type="number"
              size="small"
              value={count}
              onChange={(e) =>
                setCount(
                  Math.max(1, Math.min(300, parseInt(e.target.value, 10) || 1))
                )
              }
              inputProps={{ min: 1, max: 300 }}
              sx={{ flex: 1 }}
            />
            <FormControlSelectStartNum
              value={startNum}
              onChange={setStartNum}
            />
          </Stack>
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "rgba(0,0,0,0.6)", fontWeight: 700 }}
            >
              プレビュー：
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {previewNames.map((n, i) => (
                <Chip
                  key={`${n}-${i}`}
                  size="small"
                  label={n || "(テキスト未入力)"}
                  sx={{
                    fontWeight: 700,
                    bgcolor: text.trim() ? `${accent}14` : "rgba(0,0,0,0.06)",
                    color: text.trim() ? accent : "rgba(0,0,0,0.55)",
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          disabled={syncing || !text.trim()}
          onClick={handleSubmit}
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 800,
            bgcolor: accent,
            "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
          }}
        >
          作成
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 開始番号 01-99 の Select
function FormControlSelectStartNum({ value, onChange }) {
  const options = useMemo(
    () => Array.from({ length: 300 }, (_, i) => i + 1),
    []
  );
  return (
    <TextField
      select
      label="開始番号"
      size="small"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      sx={{ flex: 1 }}
      SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
    >
      {options.map((n) => (
        <MenuItem key={n} value={n}>
          {String(n).padStart(3, "0")}
        </MenuItem>
      ))}
    </TextField>
  );
}

// ──────────────────────────────────────────────────────────────
// Unassigned section
// ──────────────────────────────────────────────────────────────

function UnassignedSection({
  regionCounts,
  regionUsersCache,
  regionLoading,
  assignedUids,
  assignedToGroup,
  loadRegionUsers,
  selectAllInRegion,
  selectedUnassigned,
  toggleSelect,
  clearSelection,
  activeGroupId,
  activeGroup,
  onOpenMap,
  onOpenAutoAlloc,
  syncing,
  surface,
  border,
  textBase,
  accent,
  pastGroupNamesById,
}) {
  const dropdownSections = useMemo(
    () => buildDropdownSections(regionCounts),
    [regionCounts]
  );
  const flatDropdownKeys = useMemo(
    () => dropdownSections.flatMap((s) => s.items.map((it) => it.key)),
    [dropdownSections]
  );

  const [selectedRegion, setSelectedRegion] = useState("");
  useEffect(() => {
    if (selectedRegion && !flatDropdownKeys.includes(selectedRegion)) {
      setSelectedRegion("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatDropdownKeys]);

  // 選択中地域の users（cache 内）— 編成済みも含め全員表示。グレー化は UserRow 側で。
  const currentUsers = useMemo(() => {
    if (!selectedRegion) return [];
    const all = regionUsersCache[selectedRegion] || [];
    return [...all].sort((a, b) =>
      (a.name || a.id).localeCompare(b.name || b.id, "ja")
    );
  }, [selectedRegion, regionUsersCache]);

  // 2025後期グループでクラスタリング（参加経験ありが上、新規が下）
  // sortMode === "locality" は地域に応じて 市区町村 / 州 / 国 を使う
  const [sortMode, setSortMode] = useState("groups2025"); // "groups2025" | "locality"
  const localeLabel = getLocaleLabel(selectedRegion);

  const buckets = useMemo(() => {
    const m = new Map();
    currentUsers.forEach((u) => {
      const key =
        sortMode === "locality"
          ? getUserLocale(u) || ""
          : u.groups2025SecondHalf || "";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(u);
    });

    if (sortMode === "locality") {
      const withLocale = Array.from(m.entries())
        .filter(([k]) => k)
        .sort((a, b) => a[0].localeCompare(b[0], "ja"))
        .map(([loc, users]) => ({
          key: loc,
          label: loc,
          isMissing: false,
          users,
        }));
      const missing = m.has("")
        ? [
            {
              key: "__nolocale__",
              label: `${localeLabel} 未登録`,
              isMissing: true,
              users: m.get(""),
            },
          ]
        : [];
      return [...withLocale, ...missing];
    }

    // groups2025（既定）：人数の多い順、新規は下
    const withGroup = Array.from(m.entries())
      .filter(([gid]) => gid)
      .sort((a, b) => b[1].length - a[1].length)
      .map(([gid, users]) => ({
        key: gid,
        label: `2025後期グループ ${gid}`,
        isMissing: false,
        users,
      }));
    const newcomers = m.has("")
      ? [
          {
            key: "__new__",
            label: "新規",
            isMissing: true,
            users: m.get(""),
          },
        ]
      : [];
    return [...withGroup, ...newcomers];
  }, [currentUsers, sortMode, localeLabel]);

  // バケット境界をまたぐ通し番号（未編入だけカウント。編成済みはスキップ）
  const userNumbers = useMemo(() => {
    const m = new Map();
    let i = 0;
    buckets.forEach((b) =>
      b.users.forEach((u) => {
        if (!assignedUids.has(u.id)) m.set(u.id, ++i);
      })
    );
    return m;
  }, [buckets, assignedUids]);

  const isLoadingCurrent = !!regionLoading[selectedRegion];
  const isCached =
    !!selectedRegion && regionUsersCache[selectedRegion] !== undefined;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        border: `1px solid ${border}`,
        bgcolor: surface,
        boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        mb: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1.5 }}
        flexWrap="wrap"
        useFlexGap
      >
        <GroupsRoundedIcon sx={{ color: accent }} />
        <Typography sx={{ fontWeight: 700, color: textBase }}>
          未割り当てユーザー
        </Typography>
      </Stack>

      {/* 仮追加先の状態バー */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          mb: 1.5,
          borderRadius: 2,
          borderColor: activeGroupId ? accent : "rgba(0,0,0,0.12)",
          bgcolor: activeGroupId ? `${accent}07` : "transparent",
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          {activeGroupId && activeGroup ? (
            <>
              <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
                仮追加先：
              </Typography>
              <Chip
                size="small"
                label={activeGroup.name}
                sx={{
                  fontWeight: 800,
                  bgcolor: `${accent}14`,
                  color: accent,
                  border: `1px solid ${accent}33`,
                }}
              />
              <Typography sx={{ fontWeight: 700, color: textBase }}>
                {selectedUnassigned.size} 人を仮追加中
              </Typography>
              {selectedUnassigned.size > 0 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={clearSelection}
                  disabled={syncing}
                  sx={{ textTransform: "none" }}
                >
                  選択解除
                </Button>
              )}
            </>
          ) : (
            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
              右の編成済みグループからラジオボタンで「仮追加先」を選んでください。
            </Typography>
          )}
        </Stack>
      </Paper>

      {dropdownSections.length === 0 ? (
        <Typography sx={{ color: "rgba(0,0,0,0.55)", py: 2 }}>
          集計ドキュメントに「参加する」回答者がまだいません。
        </Typography>
      ) : (
        <>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 1.5 }}
          >
            <Select
              size="small"
              displayEmpty
              value={selectedRegion}
              onChange={(e) => {
                const v = e.target.value;
                clearSelection();
                setSelectedRegion(v);
                if (v) loadRegionUsers(v);
              }}
              sx={{ minWidth: 320 }}
              renderValue={(v) => {
                if (!v)
                  return (
                    <em style={{ color: "rgba(0,0,0,0.55)" }}>地域を選択…</em>
                  );
                const item = dropdownSections
                  .flatMap((s) => s.items)
                  .find((it) => it.key === v);
                if (!item) return v;
                return `${item.label}（${item.assigned} / ${item.unassigned}）`;
              }}
            >
              <MenuItem value="">
                <em>選択を解除</em>
              </MenuItem>
              {dropdownSections.flatMap((sec) => [
                <ListSubheader
                  key={`h-${sec.name}`}
                  sx={{
                    bgcolor: `${accent}10`,
                    color: accent,
                    fontWeight: 800,
                    lineHeight: 1.8,
                  }}
                >
                  {sec.name}
                </ListSubheader>,
                ...sec.items.map((it) => (
                  <MenuItem key={it.key} value={it.key}>
                    {it.label}（{it.assigned} / {it.unassigned}）
                  </MenuItem>
                )),
              ])}
            </Select>
            {selectedRegion &&
              selectedRegion !== MUHOUMATSU_KEY &&
              currentUsers.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<MapRoundedIcon />}
                onClick={() => {
                  const mode =
                    selectedRegion === "海外（アメリカ）"
                      ? "us"
                      : selectedRegion === "海外（その他）"
                      ? "world"
                      : "jp";
                  onOpenMap({
                    mode,
                    regionKey: selectedRegion,
                    users: currentUsers,
                  });
                }}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  borderColor: accent,
                  color: accent,
                }}
              >
                {selectedRegion === "海外（アメリカ）"
                  ? "アメリカ"
                  : selectedRegion === "海外（その他）"
                  ? "世界"
                  : selectedRegion}
                のマップを開く
              </Button>
            )}
            {selectedRegion &&
              !selectedRegion.startsWith("海外") &&
              selectedRegion !== MUHOUMATSU_KEY &&
              (() => {
                const unassigned = currentUsers.filter(
                  (u) => !assignedUids.has(u.id)
                );
                if (unassigned.length < 5) return null;
                return (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AutoFixHighRoundedIcon />}
                    onClick={() =>
                      onOpenAutoAlloc({
                        prefecture: selectedRegion,
                        users: unassigned,
                      })
                    }
                    disabled={syncing}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                      bgcolor: accent,
                      color: "#fff",
                      "&:hover": { bgcolor: accent, opacity: 0.92 },
                    }}
                  >
                    自動振り分け仮提案（{unassigned.length}人）
                  </Button>
                );
              })()}
          </Stack>

          {selectedRegion && currentUsers.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1.5 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                variant="caption"
                sx={{ color: "rgba(0,0,0,0.6)", fontWeight: 700 }}
              >
                並び順：
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={sortMode}
                onChange={(_, v) => {
                  if (v) setSortMode(v);
                }}
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontWeight: 700,
                    py: 0.25,
                    px: 1.25,
                  },
                  "& .Mui-selected": {
                    bgcolor: `${accent}14 !important`,
                    color: `${accent} !important`,
                  },
                }}
              >
                <ToggleButton value="groups2025">2025後期グループ</ToggleButton>
                <ToggleButton value="locality">{localeLabel}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}

          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, borderColor: border }}
          >
            {!selectedRegion ? (
              <Typography
                sx={{ color: "rgba(0,0,0,0.55)", p: 2, textAlign: "center" }}
              >
                上のドロップダウンから地域を選択してください。
              </Typography>
            ) : isLoadingCurrent ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            ) : !isCached ? (
              <Typography
                sx={{ color: "rgba(0,0,0,0.55)", p: 2, textAlign: "center" }}
              >
                データを読み込んでいます…
              </Typography>
            ) : currentUsers.length === 0 ? (
              <Typography
                sx={{ color: "rgba(0,0,0,0.55)", p: 2, textAlign: "center" }}
              >
                この地域に未割り当てのユーザーはいません。
              </Typography>
            ) : (
              <Box sx={{ p: 1 }}>
                {buckets.map((b) => (
                  <Paper
                    key={b.key}
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      borderRadius: 2,
                      borderColor: b.isMissing
                        ? "#b0880055"
                        : `${accent}55`,
                      borderStyle: b.isMissing ? "dashed" : "solid",
                      bgcolor: b.isMissing ? "#b0880008" : `${accent}06`,
                      overflow: "hidden",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ px: 1.5, py: 0.75 }}
                    >
                      <Chip
                        size="small"
                        label={b.label}
                        sx={{
                          fontWeight: 800,
                          ...(b.isMissing
                            ? {
                                bgcolor: "#b0880014",
                                color: "#b08800",
                                border: "1px solid #b0880033",
                              }
                            : {
                                bgcolor: `${accent}14`,
                                color: accent,
                                border: `1px solid ${accent}33`,
                              }),
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(0,0,0,0.55)" }}
                      >
                        {b.users.length}人
                      </Typography>
                    </Stack>
                    <Divider sx={{ borderColor: "rgba(0,0,0,0.06)" }} />
                    <Stack spacing={0.25} sx={{ p: 0.5 }}>
                      {b.users.map((u) => {
                        const assignedInfo = assignedToGroup.get(u.id);
                        return (
                          <UserRow
                            key={u.id}
                            user={u}
                            number={userNumbers.get(u.id)}
                            checked={selectedUnassigned.has(u.id)}
                            onToggle={
                              assignedInfo ? null : () => toggleSelect(u.id)
                            }
                            isAssigned={!!assignedInfo}
                            assignedGroupName={assignedInfo?.groupName}
                            textBase={textBase}
                            accent={accent}
                            pastGroupName={
                              sortMode === "locality" && u.groups2025SecondHalf
                                ? pastGroupNamesById[u.groups2025SecondHalf] ||
                                  u.groups2025SecondHalf
                                : ""
                            }
                          />
                        );
                      })}
                    </Stack>
                  </Paper>
                ))}
              </Box>
            )}
          </Paper>
        </>
      )}
    </Paper>
  );
}

// 血液型ごとの色（小さい丸バッジの背景）
const BLOOD_COLORS = {
  A: "#e57373",
  B: "#ffb74d",
  O: "#81c784",
  AB: "#9575cd",
  わからない: "#bdbdbd",
};

// 1ユーザー行：番号＋アバター＋名前＋uid＋性別／血液型／作成日＋MMM参加履歴＋チェックボックス
function UserRow({
  user,
  number,
  checked,
  onToggle,
  isAssigned,
  assignedGroupName,
  textBase,
  accent,
  pastGroupName,
}) {
  const phases = mmmPhases(user.payments);
  const joinedAt = formatJoinedAt(user.createdAtMs);
  // 番号列・チェックボックス列を確保するか（左パネルのリストでのみ true）
  const inSelectionList =
    typeof number === "number" || !!onToggle || !!isAssigned;
  const canSelect = !!onToggle && !isAssigned;

  const renderGender = () => {
    if (user.sextype === "男性")
      return (
        <Tooltip title="男性">
          <MaleRoundedIcon sx={{ fontSize: 16, color: "#1976d2" }} />
        </Tooltip>
      );
    if (user.sextype === "女性")
      return (
        <Tooltip title="女性">
          <FemaleRoundedIcon sx={{ fontSize: 16, color: "#e91e63" }} />
        </Tooltip>
      );
    if (user.sextype === "回答しない")
      return (
        <Tooltip title="性別：回答しない">
          <HelpOutlineRoundedIcon
            sx={{ fontSize: 14, color: "rgba(0,0,0,0.45)" }}
          />
        </Tooltip>
      );
    return null;
  };

  const renderBlood = () => {
    if (!user.bloodtype) return null;
    const bg = BLOOD_COLORS[user.bloodtype] || "#bdbdbd";
    const label = user.bloodtype === "わからない" ? "?" : user.bloodtype;
    return (
      <Tooltip
        title={
          user.bloodtype === "わからない"
            ? "血液型：わからない"
            : `${user.bloodtype}型`
        }
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 18,
            height: 18,
            px: 0.5,
            borderRadius: "999px",
            bgcolor: bg,
            color: "#fff",
            fontSize: 10,
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          {label}
        </Box>
      </Tooltip>
    );
  };
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={canSelect ? onToggle : undefined}
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 1,
        opacity: isAssigned ? 0.45 : 1,
        cursor: canSelect ? "pointer" : "default",
        userSelect: canSelect ? "none" : "auto",
        "&:hover": {
          bgcolor: isAssigned ? "transparent" : "rgba(0,0,0,0.03)",
        },
      }}
    >
      {inSelectionList && (
        <Box sx={{ width: 28, flexShrink: 0, textAlign: "right" }}>
          {typeof number === "number" && (
            <Typography
              variant="caption"
              sx={{
                color: "rgba(0,0,0,0.55)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {number}.
            </Typography>
          )}
        </Box>
      )}
      {onToggle ? (
        <Checkbox
          size="small"
          checked={!!checked}
          onClick={(e) => e.stopPropagation()}
          onChange={onToggle}
          sx={{
            color: accent,
            "&.Mui-checked": { color: accent },
            p: 0.5,
          }}
        />
      ) : (
        inSelectionList && <Box sx={{ width: 30, flexShrink: 0 }} />
      )}
      <Avatar
        src={user.avatar || undefined}
        alt={user.name || "user"}
        sx={{
          width: 32,
          height: 32,
          border: "1px solid rgba(0,0,0,0.08)",
          fontSize: 13,
        }}
      >
        {(user.name || "?").slice(0, 1)}
      </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography
            sx={{ fontWeight: 600, color: textBase, minWidth: 0 }}
            noWrap
          >
            {user.name || "(名前未設定)"}
          </Typography>
          {renderGender()}
          {renderBlood()}
        </Stack>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(0,0,0,0.45)",
            display: "block",
            lineHeight: 1.2,
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: 10,
          }}
          noWrap
        >
          {user.id}
        </Typography>
        {(() => {
          const locale = getUserLocale(user);
          if (!locale && !joinedAt && !pastGroupName) return null;
          return (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ mt: 0.25 }}
              flexWrap="wrap"
              useFlexGap
            >
              {locale && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(0,0,0,0.7)",
                    lineHeight: 1.2,
                    fontWeight: 600,
                  }}
                >
                  {locale}
                </Typography>
              )}
              {pastGroupName && (
                <Chip
                  size="small"
                  label={`2025後期：${pastGroupName}`}
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor: `${accent}10`,
                    color: accent,
                    border: `1px solid ${accent}33`,
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              )}
              {joinedAt && (
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(0,0,0,0.55)", lineHeight: 1.2 }}
                >
                  {locale || pastGroupName ? "· " : ""}
                  {joinedAt}
                </Typography>
              )}
            </Stack>
          );
        })()}
      </Box>
      {isAssigned && assignedGroupName && (
        <Chip
          size="small"
          label={`編成済：${assignedGroupName}`}
          sx={{
            fontWeight: 700,
            fontSize: 11,
            height: 22,
            bgcolor: "rgba(0,0,0,0.06)",
            color: "rgba(0,0,0,0.65)",
            border: "1px solid rgba(0,0,0,0.12)",
          }}
        />
      )}
      {phases.length > 0 && (
        <Chip
          size="small"
          label={`MMM ${phases.join(",")}`}
          sx={{
            fontWeight: 700,
            fontSize: 11,
            height: 22,
            bgcolor: "rgba(0,0,0,0.05)",
            color: "rgba(0,0,0,0.7)",
          }}
        />
      )}
    </Stack>
  );
}

// ──────────────────────────────────────────────────────────────
// Groups section
// ──────────────────────────────────────────────────────────────

function GroupsSection({
  visibleGroups,
  openGroups,
  toggleGroupOpen,
  removeMemberFromGroup,
  moveMemberToGroup,
  allGroupOptions,
  renameDraft,
  setRenameDraftFor,
  renameGroup,
  toggleFinalize,
  removeGroup,
  onCreateClick,
  activeGroupId,
  toggleActiveGroupId,
  selectedUnassigned,
  usersInCache,
  syncing,
  surface,
  border,
  textBase,
  accent,
}) {
  // 仮追加対象のユーザーオブジェクト配列（active グループにだけ渡す）
  const stagedUsers = useMemo(() => {
    if (!activeGroupId) return null;
    const list = Array.from(selectedUnassigned)
      .map((uid) => usersInCache.get(uid))
      .filter(Boolean);
    return list.length > 0 ? list : null;
  }, [activeGroupId, selectedUnassigned, usersInCache]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        border: `1px solid ${border}`,
        bgcolor: surface,
        boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        mb: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1.5 }}
        flexWrap="wrap"
        useFlexGap
      >
        <GroupsRoundedIcon sx={{ color: accent }} />
        <Typography sx={{ fontWeight: 700, color: textBase }}>
          編成済みグループ（{visibleGroups.length}グループ）
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          variant="contained"
          startIcon={<AddRoundedIcon />}
          disabled={syncing}
          onClick={onCreateClick}
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 800,
            bgcolor: accent,
            "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
          }}
        >
          新規グループ作成
        </Button>
      </Stack>

      {visibleGroups.length === 0 ? (
        <Typography sx={{ color: "rgba(0,0,0,0.55)", py: 2 }}>
          まだグループはありません。右上の「新規グループ作成」から作成してください。
        </Typography>
      ) : (
        <Stack spacing={1}>
          {visibleGroups.map((g) => {
            const isActive = activeGroupId === g.id;
            return (
              <GroupCard
                key={g.id}
                group={g}
                isOpen={openGroups.has(g.id)}
                isActive={isActive}
                stagedUsers={isActive ? stagedUsers : null}
                renameDraftValue={renameDraft[g.id]}
                allGroupOptions={allGroupOptions}
                syncing={syncing}
                border={border}
                textBase={textBase}
                accent={accent}
                onToggleOpen={toggleGroupOpen}
                onToggleActive={toggleActiveGroupId}
                onSetRenameDraft={setRenameDraftFor}
                onRenameGroup={renameGroup}
                onToggleFinalize={toggleFinalize}
                onRemoveGroup={removeGroup}
                onRemoveMember={removeMemberFromGroup}
                onMoveMember={moveMemberToGroup}
              />
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}

// ──────────────────────────────────────────────────────────────
// GroupCard（React.memo で再レンダリング抑制。800+グループ用）
// 親の左パネル state（selectedUnassigned 等）が変わったとき、
// この card のいずれかのpropが変わってない限り再描画されない。
// ──────────────────────────────────────────────────────────────

const GroupCard = React.memo(function GroupCard({
  group,
  isOpen,
  isActive,
  stagedUsers,
  renameDraftValue,
  allGroupOptions,
  syncing,
  border,
  textBase,
  accent,
  onToggleOpen,
  onToggleActive,
  onSetRenameDraft,
  onRenameGroup,
  onToggleFinalize,
  onRemoveGroup,
  onRemoveMember,
  onMoveMember,
}) {
  const g = group;
  const finalized = g.status === "finalized";
  const stagedHere = stagedUsers || [];
  const moveOptions = useMemo(
    () => allGroupOptions.filter((opt) => opt.id !== g.id),
    [allGroupOptions, g.id]
  );

  return (
    <>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: isActive
                    ? accent
                    : finalized
                    ? accent
                    : border,
                  borderWidth: isActive ? 2 : finalized ? 1.5 : 1,
                  bgcolor: isActive ? `${accent}05` : "transparent",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ p: 1.2, cursor: "pointer" }}
                  onClick={() => onToggleOpen(g.id)}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Radio
                    size="small"
                    checked={isActive}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleActive(g.id)}
                    sx={{
                      color: accent,
                      "&.Mui-checked": { color: accent },
                      p: 0.5,
                    }}
                  />
                  <IconButton size="small">
                    {isOpen ? (
                      <ExpandLessRoundedIcon />
                    ) : (
                      <ExpandMoreRoundedIcon />
                    )}
                  </IconButton>
                  <Typography sx={{ fontWeight: 700, color: textBase }}>
                    {g.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${g.members.length}人`}
                    sx={{
                      fontWeight: 700,
                      bgcolor: `${accent}14`,
                      color: accent,
                    }}
                  />
                  {finalized ? (
                    <Chip
                      size="small"
                      icon={
                        <CheckCircleRoundedIcon style={{ color: "#2e7d32" }} />
                      }
                      label="確定"
                      sx={{
                        fontWeight: 800,
                        bgcolor: "#2e7d3214",
                        color: "#2e7d32",
                        border: "1px solid #2e7d3233",
                      }}
                    />
                  ) : (
                    <Chip
                      size="small"
                      label="下書き"
                      sx={{
                        fontWeight: 800,
                        bgcolor: "rgba(0,0,0,0.06)",
                        color: "rgba(0,0,0,0.65)",
                      }}
                    />
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    size="small"
                    variant={
                      finalized && stagedHere.length === 0
                        ? "outlined"
                        : "contained"
                    }
                    startIcon={
                      finalized && stagedHere.length === 0 ? (
                        <RestoreRoundedIcon />
                      ) : (
                        <CheckCircleRoundedIcon />
                      )
                    }
                    disabled={syncing}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleFinalize(g.id);
                    }}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 800,
                      ...(finalized && stagedHere.length === 0
                        ? {}
                        : {
                            bgcolor: accent,
                            "&:hover": {
                              bgcolor: accent,
                              filter: "brightness(0.95)",
                            },
                          }),
                    }}
                  >
                    {stagedHere.length > 0
                      ? `${stagedHere.length}人を追加して確定`
                      : finalized
                      ? "差し戻す"
                      : "確定する"}
                  </Button>
                  <Tooltip title="グループを削除">
                    <span>
                      <IconButton
                        size="small"
                        disabled={syncing}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveGroup(g.id);
                        }}
                      >
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Divider />
                  <Box sx={{ p: 1.5 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <TextField
                        size="small"
                        label="グループ名"
                        value={
                          renameDraftValue !== undefined
                            ? renameDraftValue
                            : g.name
                        }
                        onChange={(e) =>
                          onSetRenameDraft(g.id, e.target.value)
                        }
                        sx={{ minWidth: 220 }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={
                          syncing ||
                          renameDraftValue === undefined ||
                          renameDraftValue.trim() === g.name
                        }
                        onClick={() => onRenameGroup(g.id)}
                        sx={{
                          borderRadius: 999,
                          textTransform: "none",
                          fontWeight: 700,
                        }}
                      >
                        名前変更を保存
                      </Button>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(0,0,0,0.55)" }}
                      >
                        地域：{g.region}
                      </Typography>
                    </Stack>

                    {g.members.length === 0 && stagedHere.length === 0 ? (
                      <Typography sx={{ color: "rgba(0,0,0,0.55)", py: 1 }}>
                        {isActive
                          ? "左のリストでユーザーを選択すると、ここに仮追加されます。"
                          : "メンバーがいません。"}
                      </Typography>
                    ) : (
                      <Stack spacing={0.25}>
                        {g.members.map((m) => (
                          <Stack
                            key={m.uid}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
                            }}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Box sx={{ flexGrow: 1, minWidth: 220 }}>
                              <UserRow
                                user={memberToUserShape(m)}
                                checked={false}
                                onToggle={null}
                                textBase={textBase}
                                accent={accent}
                              />
                            </Box>
                            {moveOptions.length > 0 && (
                              <Select
                                size="small"
                                displayEmpty
                                value=""
                                onChange={(e) =>
                                  onMoveMember(g.id, m.uid, e.target.value)
                                }
                                sx={{ minWidth: 200 }}
                              >
                                <MenuItem value="">
                                  <em>別グループへ移動…</em>
                                </MenuItem>
                                {moveOptions.map((opt) => (
                                  <MenuItem key={opt.id} value={opt.id}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                            <Tooltip title="未割り当てに戻す">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={syncing}
                                  onClick={() =>
                                    onRemoveMember(g.id, m.uid)
                                  }
                                >
                                  <CloseRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        ))}
                      </Stack>
                    )}

                    {/* 仮追加分の表示（このグループが active の場合のみ） */}
                    {isActive && stagedHere.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Divider sx={{ mb: 1 }} />
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 0.5 }}
                        >
                          <Chip
                            size="small"
                            label={`仮追加 ${stagedHere.length}人`}
                            sx={{
                              fontWeight: 800,
                              bgcolor: "#b0880014",
                              color: "#b08800",
                              border: "1px solid #b0880033",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: "rgba(0,0,0,0.55)" }}
                          >
                            「確定する」ボタンで本追加されます
                          </Typography>
                        </Stack>
                        <Stack
                          spacing={0.25}
                          sx={{
                            border: "1px dashed #b0880055",
                            bgcolor: "#b0880006",
                            borderRadius: 1,
                            p: 0.5,
                          }}
                        >
                          {stagedHere.map((u) => (
                            <UserRow
                              key={u.id}
                              user={u}
                              checked={false}
                              onToggle={null}
                              textBase={textBase}
                              accent={accent}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Paper>
    </>
  );
});

// ──────────────────────────────────────────────────────────────
// Map dialog
//   mode === "jp"    都道府県マップ。市区町村中心点に円マーカー
//   mode === "us"    アメリカ州マップ
//   mode === "world" 世界マップ（国別マーカー、日本・米国は除外）
// ──────────────────────────────────────────────────────────────

function markerRadius(count) {
  return Math.min(38, 6 + Math.sqrt(count) * 4);
}

// 人数段階の色（YlOrBr 5階調・ColorBrewer 由来）
const COUNT_TIERS = [
  { min: 1, max: 1, color: "#fff7bc", label: "1人" },
  { min: 2, max: 3, color: "#fec44f", label: "2〜3人" },
  { min: 4, max: 7, color: "#fe9929", label: "4〜7人" },
  { min: 8, max: 15, color: "#ec7014", label: "8〜15人" },
  { min: 16, max: Infinity, color: "#cc4c02", label: "16人以上" },
];

function colorForCount(count) {
  for (const t of COUNT_TIERS) {
    if (count >= t.min && count <= t.max) return t.color;
  }
  return "#bdbdbd";
}

// ──────────────────────────────────────────────────────────────
// 自動振り分け仮提案ダイアログ
// ──────────────────────────────────────────────────────────────

function DraggableMemberRow({
  m,
  groupIdx,
  memberIdx,
  onSwap,
  pastGroupNamesById,
  accent,
  textBase,
  isCityMismatch,
}) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DND_MEMBER_TYPE,
      item: { groupIdx, memberIdx },
      collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    }),
    [groupIdx, memberIdx]
  );
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DND_MEMBER_TYPE,
      canDrop: (item) =>
        !(item.groupIdx === groupIdx && item.memberIdx === memberIdx),
      drop: (item) =>
        onSwap(item.groupIdx, item.memberIdx, groupIdx, memberIdx),
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
        canDrop: !!monitor.canDrop(),
      }),
    }),
    [groupIdx, memberIdx, onSwap]
  );
  const combinedRef = (node) => {
    drag(node);
    drop(node);
  };

  return (
    <Stack
      ref={combinedRef}
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        pl: 0.25,
        py: 0.25,
        borderRadius: 1,
        opacity: isDragging ? 0.35 : 1,
        bgcolor:
          isOver && canDrop
            ? `${accent}28`
            : isOver
            ? "rgba(244,67,54,0.1)"
            : isCityMismatch
            ? "rgba(244,67,54,0.08)"
            : "transparent",
        border: isCityMismatch ? "1px solid rgba(244,67,54,0.45)" : "1px solid transparent",
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
        transition: "background-color 0.12s ease",
        flexWrap: "wrap",
        useFlexGap: true,
      }}
    >
      <DragIndicatorRoundedIcon
        sx={{ fontSize: 14, color: "rgba(0,0,0,0.3)", flexShrink: 0 }}
      />
      <Avatar
        src={m.avatar || undefined}
        sx={{
          width: 22,
          height: 22,
          fontSize: 11,
          bgcolor: `${accent}30`,
          flexShrink: 0,
        }}
      >
        {(m.name || "?").slice(0, 1)}
      </Avatar>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: textBase, minWidth: 0 }}
        noWrap
      >
        {m.name || "(名前未設定)"}
      </Typography>
      {isCityMismatch && (
        <Tooltip title={`${m.prefecture || "(県未設定)"} に「${m.city}」は存在しません`}>
          <Chip
            size="small"
            icon={<WarningAmberRoundedIcon sx={{ fontSize: 13 }} />}
            label="県市不一致"
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 800,
              bgcolor: "#d32f2f",
              color: "#fff",
              "& .MuiChip-label": { px: 0.75 },
              "& .MuiChip-icon": { color: "#fff", ml: 0.25 },
            }}
          />
        </Tooltip>
      )}
      {m.city && (
        <Chip
          size="small"
          label={m.city}
          sx={{
            height: 18,
            fontSize: 10,
            bgcolor: isCityMismatch ? "rgba(244,67,54,0.18)" : "rgba(0,0,0,0.06)",
            color: isCityMismatch ? "#c62828" : "rgba(0,0,0,0.7)",
            fontWeight: isCityMismatch ? 700 : 400,
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      )}
      {m.groups2025SecondHalf && (
        <Chip
          size="small"
          label={`2025後期：${
            pastGroupNamesById?.[m.groups2025SecondHalf] ||
            m.groups2025SecondHalf
          }`}
          sx={{
            height: 18,
            fontSize: 10,
            bgcolor: `${accent}10`,
            color: accent,
            fontWeight: 700,
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      )}
      {m.sextype && (
        <Chip
          size="small"
          label={m.sextype}
          sx={{
            height: 18,
            fontSize: 10,
            bgcolor: "rgba(0,0,0,0.04)",
            color: "rgba(0,0,0,0.6)",
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      )}
    </Stack>
  );
}

function AutoAllocationDialog({
  open,
  onClose,
  prefecture,
  users,
  pastGroupNamesById,
  onExecute,
  onOpenMap,
  syncing,
  accent,
  textBase,
  border,
}) {
  const [opts, setOpts] = useState({
    sameCityCluster: true,
    past2025Diverse: true,
    sextypeBloodDiverse: true,
  });
  const [seed, setSeed] = useState(0); // 「再計算」用
  const [proposal, setProposal] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [citiesByPref, setCitiesByPref] = useState(null);
  const [computing, setComputing] = useState(false);
  const [progress, setProgress] = useState({
    phase: "",
    percent: 0,
    message: "",
    elapsedMs: 0,
    etaMs: 0,
  });

  // 手動で動かした swap の履歴（undo 用）。
  // 各要素 = { fromGi, fromMi, toGi, toMi }
  const [swapHistory, setSwapHistory] = useState([]);

  // ドラッグ＆ドロップで仮グループ間のメンバーを 1:1 で入れ替え
  const handleSwap = useCallback(
    (fromGi, fromMi, toGi, toMi) => {
      if (fromGi === toGi && fromMi === toMi) return;
      setProposal((p) => {
        if (!p) return p;
        const groups = p.groups.map((g) => g.slice());
        const tmp = groups[fromGi][fromMi];
        groups[fromGi][fromMi] = groups[toGi][toMi];
        groups[toGi][toMi] = tmp;
        return { ...p, groups };
      });
      setSwapHistory((prev) => [
        ...prev,
        { fromGi, fromMi, toGi, toMi },
      ]);
    },
    []
  );

  // 直前の swap を取り消す
  const handleUndoSwap = useCallback(() => {
    setSwapHistory((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setProposal((p) => {
        if (!p) return p;
        const groups = p.groups.map((g) => g.slice());
        const tmp = groups[last.fromGi][last.fromMi];
        groups[last.fromGi][last.fromMi] = groups[last.toGi][last.toMi];
        groups[last.toGi][last.toMi] = tmp;
        return { ...p, groups };
      });
      return prev.slice(0, -1);
    });
  }, []);

  // japanCities をダイアログオープン時に取得（HTTPキャッシュ前提）
  useEffect(() => {
    if (!open || citiesByPref) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/data/japanCities.json", {
          cache: "force-cache",
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const json = await res.json();
        if (!cancelled) setCitiesByPref(json || {});
      } catch (e) {
        console.warn("japanCities.json load (auto-alloc) failed:", e);
        if (!cancelled) setCitiesByPref({}); // 空でも先に進める
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, citiesByPref]);

  // 開かれたら初期計算、 opts / seed / citiesByPref 変化で再計算
  useEffect(() => {
    if (!open || !users || users.length === 0) {
      setProposal(null);
      setComputing(false);
      return;
    }
    // sameCityCluster を活かすには japanCities が必要。未ロードなら待つ。
    if (opts.sameCityCluster && citiesByPref === null) {
      return;
    }

    let cancelled = false;
    setComputing(true);
    setSwapHistory([]); // 新規計算で手動 swap 履歴はクリア
    setProgress({
      phase: "init",
      percent: 0,
      message: "計算開始…",
      elapsedMs: 0,
      etaMs: 0,
    });

    (async () => {
      try {
        const result = await autoAllocateGroups(
          users,
          opts,
          citiesByPref || {},
          prefecture,
          (p) => {
            if (!cancelled) setProgress(p);
          }
        );
        if (!cancelled) {
          setProposal(result);
        }
      } catch (e) {
        console.error("auto allocate error:", e);
      } finally {
        if (!cancelled) setComputing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, opts, seed, users, citiesByPref, prefecture]);

  const handleToggle = (key) => {
    setOpts((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleRecalc = () => setSeed((s) => s + 1);

  // 県の市区町村マスタが揃ってる場合、有効な市区町村名 Set を作る。
  // city が空、もしくは Set に含まれない場合「県市不一致」。
  const validCitySet = useMemo(() => {
    const set = new Set();
    if (citiesByPref && prefecture && citiesByPref[prefecture]) {
      citiesByPref[prefecture].forEach((c) => {
        if (c?.name) set.add(c.name);
      });
    }
    return set;
  }, [citiesByPref, prefecture]);
  const checkMismatch = (u) => {
    if (!u?.city) return false;
    if (validCitySet.size === 0) return false; // マスタ未ロードなら判定しない
    return !validCitySet.has(u.city);
  };
  const mismatchCount = useMemo(() => {
    if (!proposal) return 0;
    let n = 0;
    proposal.groups.forEach((g) => g.forEach((u) => checkMismatch(u) && n++));
    proposal.leftover.forEach((u) => checkMismatch(u) && n++);
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal, validCitySet]);

  const handleExecute = async () => {
    if (!proposal || !proposal.groups.length) return;
    if (
      !window.confirm(
        `${prefecture}に ${proposal.groups.length} グループ（仮）を作成します。よろしいですか？`
      )
    ) {
      return;
    }
    setExecuting(true);
    try {
      const res = await onExecute({
        prefecture,
        allocatedGroups: proposal.groups,
      });
      if (res?.ok) {
        onClose();
      }
    } finally {
      setExecuting(false);
    }
  };

  const sizes = proposal?.sizes;

  return (
    <DndProvider backend={HTML5Backend}>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>自動振り分け仮提案 — {prefecture}</span>
        <IconButton onClick={onClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${border}`,
            bgcolor: "#fafafa",
          }}
        >
          <Typography variant="body2" sx={{ color: textBase, mb: 1 }}>
            未割り当て {users?.length || 0} 人
            {sizes && (
              <>
                {" "}
                → 6人グループ <b>{sizes.sixes}</b>つ + 5人グループ{" "}
                <b>{sizes.fives}</b>つ ={" "}
                <b>計 {sizes.sixes + sizes.fives} グループ</b>
                （残り {sizes.leftover} 人）
              </>
            )}
            {mismatchCount > 0 && (
              <Chip
                size="small"
                icon={<WarningAmberRoundedIcon sx={{ fontSize: 13 }} />}
                label={`県市不一致 ${mismatchCount}人`}
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: 11,
                  fontWeight: 800,
                  bgcolor: "#d32f2f",
                  color: "#fff",
                  "& .MuiChip-icon": { color: "#fff", ml: 0.25 },
                }}
              />
            )}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={opts.sameCityCluster}
                  onChange={() => handleToggle("sameCityCluster")}
                />
              }
              label={
                <Typography variant="body2">
                  近い市区町村でまとめる（地理的クラスタリング）
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={opts.past2025Diverse}
                  onChange={() => handleToggle("past2025Diverse")}
                />
              }
              label={
                <Typography variant="body2">
                  2025後期グループでばらす（新しい繋がり）
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={opts.sextypeBloodDiverse}
                  onChange={() => handleToggle("sextypeBloodDiverse")}
                />
              }
              label={
                <Typography variant="body2">
                  性別・血液型でばらす
                </Typography>
              }
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              variant="outlined"
              startIcon={<UndoRoundedIcon />}
              onClick={handleUndoSwap}
              disabled={swapHistory.length === 0 || computing}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: swapHistory.length > 0 ? accent : undefined,
                borderColor: swapHistory.length > 0 ? accent : undefined,
              }}
            >
              戻る{swapHistory.length > 0 ? `（${swapHistory.length}）` : ""}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ShuffleRoundedIcon />}
              onClick={handleRecalc}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              再計算
            </Button>
          </Stack>
        </Box>

        <Box sx={{ p: 1.5 }}>
          {computing ? (
            <Box sx={{ py: 5, px: 3 }}>
              <Stack spacing={2} alignItems="center">
                <CircularProgress size={32} sx={{ color: accent }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: textBase }}
                >
                  {progress.message || "計算中…"}
                </Typography>
                <Box sx={{ width: "100%", maxWidth: 480 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.max(0, Math.min(100, progress.percent || 0))}
                    sx={{
                      height: 8,
                      borderRadius: 999,
                      "& .MuiLinearProgress-bar": { bgcolor: accent },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.75,
                      color: "rgba(0,0,0,0.55)",
                      fontWeight: 700,
                    }}
                  >
                    {Math.round(progress.percent || 0)}%
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(0,0,0,0.55)", textAlign: "center" }}
                >
                  人数が多いと最適化に時間がかかります。
                  そのまま少々お待ちください。
                </Typography>
              </Stack>
            </Box>
          ) : !proposal || proposal.groups.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                color: "rgba(0,0,0,0.55)",
                textAlign: "center",
                py: 4,
              }}
            >
              未割り当てが 5 人未満なので仮グループを作れません。
            </Typography>
          ) : (
            <Stack spacing={1}>
              {proposal.groups.map((members, idx) => (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    borderColor: `${accent}55`,
                    bgcolor: `${accent}06`,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 0.5 }}
                  >
                    <Chip
                      size="small"
                      label={`仮グループ ${idx + 1}`}
                      sx={{
                        bgcolor: accent,
                        color: "#fff",
                        fontWeight: 700,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(0,0,0,0.6)", fontWeight: 700 }}
                    >
                      {members.length} 人
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {onOpenMap && (
                      <Button
                        size="small"
                        startIcon={<MapRoundedIcon />}
                        onClick={() =>
                          onOpenMap({
                            mode: "jp",
                            regionKey: prefecture,
                            users: members,
                            titleSuffix: `仮グループ ${idx + 1}`,
                          })
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          color: accent,
                          py: 0,
                          px: 1,
                          minHeight: 24,
                          fontSize: 12,
                        }}
                      >
                        マップ
                      </Button>
                    )}
                  </Stack>
                  <Stack spacing={0.25}>
                    {members.map((m, mi) => (
                      <DraggableMemberRow
                        key={m.id}
                        m={m}
                        groupIdx={idx}
                        memberIdx={mi}
                        onSwap={handleSwap}
                        pastGroupNamesById={pastGroupNamesById}
                        accent={accent}
                        textBase={textBase}
                        isCityMismatch={checkMismatch(m)}
                      />
                    ))}
                  </Stack>
                </Paper>
              ))}

              {proposal.leftover.length > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    borderStyle: "dashed",
                    borderColor: "#b08800",
                    bgcolor: "#b0880008",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 0.5 }}
                  >
                    <Chip
                      size="small"
                      label="残り（未割り当てのまま）"
                      sx={{
                        bgcolor: "#b08800",
                        color: "#fff",
                        fontWeight: 700,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "#b08800", fontWeight: 700 }}
                    >
                      {proposal.leftover.length} 人
                    </Typography>
                  </Stack>
                  <Stack spacing={0.25}>
                    {proposal.leftover.map((m) => (
                      <Stack
                        key={m.id}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ pl: 0.5 }}
                      >
                        <Avatar
                          src={m.avatar || undefined}
                          sx={{ width: 22, height: 22, fontSize: 11 }}
                        >
                          {(m.name || "?").slice(0, 1)}
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: textBase }}
                          noWrap
                        >
                          {m.name || "(名前未設定)"}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }} disabled={executing || syncing}>
          キャンセル
        </Button>
        <Button
          onClick={handleExecute}
          disabled={
            executing ||
            syncing ||
            computing ||
            !proposal ||
            proposal.groups.length === 0
          }
          variant="contained"
          startIcon={
            executing ? <CircularProgress size={16} /> : <PlayArrowRoundedIcon />
          }
          sx={{
            bgcolor: accent,
            color: "#fff",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { bgcolor: accent, opacity: 0.92 },
          }}
        >
          {executing ? "作成中…" : `実行（仮グループ作成）`}
        </Button>
      </DialogActions>
    </Dialog>
    </DndProvider>
  );
}

function MapDialog({
  open,
  onClose,
  mode,
  regionKey,
  users,
  assignedToGroup,
  accent,
  titleSuffix,
}) {
  const [japanCities, setJapanCities] = useState(null);
  const [usStates, setUsStates] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [includeAssigned, setIncludeAssigned] = useState(false);

  // 開くたびに「編成済を含まない」をデフォルトに戻す
  useEffect(() => {
    if (open) setIncludeAssigned(false);
  }, [open]);

  // 集計対象ユーザー
  const targetUsers = useMemo(
    () =>
      includeAssigned
        ? users
        : users.filter((u) => !assignedToGroup.has(u.id)),
    [users, includeAssigned, assignedToGroup]
  );

  // 必要なデータをオンデマンドで取得
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      try {
        if (mode === "jp" && !japanCities) {
          setDataLoading(true);
          const res = await fetch("/data/japanCities.json");
          const data = await res.json();
          if (!cancelled) setJapanCities(data);
        } else if (mode === "us" && !usStates) {
          setDataLoading(true);
          const res = await fetch("/data/usStates.json");
          const data = await res.json();
          if (!cancelled) setUsStates(data);
        }
      } catch (e) {
        console.error("map data load error:", e);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  // bucket: ユーザーを位置キー（市区町村／州／国）で集約
  const buckets = useMemo(() => {
    const m = new Map();
    targetUsers.forEach((u) => {
      let key;
      let lookup = null;
      if (mode === "jp") {
        key = u.city || "";
        if (key && japanCities) {
          const list = japanCities[regionKey] || [];
          lookup = list.find((c) => c.name === key);
        }
      } else if (mode === "us") {
        key = u.usState || "";
        if (key && usStates) {
          const arr = usStates[key];
          if (arr && arr.length > 0) lookup = arr[0];
        }
      } else if (mode === "world") {
        key = u.country || "";
        if (key) {
          lookup = COUNTRIES_DATA.find((c) => c.label === key);
        }
      }
      if (!m.has(key)) {
        m.set(key, { key, label: key, lat: null, lng: null, users: [] });
      }
      const entry = m.get(key);
      entry.users.push(u);
      if (lookup && (entry.lat === null || entry.lng === null)) {
        entry.lat = lookup.lat;
        entry.lng = lookup.lng;
      }
    });
    return Array.from(m.values()).sort((a, b) => b.users.length - a.users.length);
  }, [targetUsers, mode, regionKey, japanCities, usStates]);

  const placedBuckets = buckets.filter(
    (b) => b.lat !== null && b.lng !== null && b.key
  );
  const orphanBuckets = buckets.filter(
    (b) => b.lat === null || b.lng === null || !b.key
  );

  // 地図の中心・初期ズーム
  const { center, zoom } = useMemo(() => {
    if (placedBuckets.length > 0) {
      const lat =
        placedBuckets.reduce((s, b) => s + b.lat, 0) / placedBuckets.length;
      const lng =
        placedBuckets.reduce((s, b) => s + b.lng, 0) / placedBuckets.length;
      const z = mode === "jp" ? 8 : mode === "us" ? 4 : 2;
      return { center: [lat, lng], zoom: z };
    }
    if (mode === "us") return { center: [39.8, -98.5], zoom: 4 };
    if (mode === "world") return { center: [20, 0], zoom: 2 };
    return { center: [36, 138], zoom: 5 };
  }, [placedBuckets, mode]);

  const baseTitle =
    mode === "us"
      ? "アメリカ マップ"
      : mode === "world"
      ? "世界マップ（日本・USを除く）"
      : `${regionKey || ""} マップ`;
  const title = titleSuffix ? `${baseTitle} — ${titleSuffix}` : baseTitle;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>{title}</span>
        <IconButton onClick={onClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "#fff",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ px: 2, py: 1 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Typography
              variant="caption"
              sx={{ color: "rgba(0,0,0,0.6)", fontWeight: 700 }}
            >
              集計対象：
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={includeAssigned ? "all" : "unassigned"}
              onChange={(_, v) => {
                if (v) setIncludeAssigned(v === "all");
              }}
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  py: 0.25,
                  px: 1.25,
                },
                "& .Mui-selected": {
                  bgcolor: `${accent}14 !important`,
                  color: `${accent} !important`,
                },
              }}
            >
              <ToggleButton value="unassigned">グループ参入済を含まない</ToggleButton>
              <ToggleButton value="all">グループ参入済を含む</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.55)" }}>
              （対象 {targetUsers.length} 人）
            </Typography>
          </Stack>
          {orphanBuckets.length > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1,
                borderTop: "1px dashed rgba(0,0,0,0.08)",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "rgba(0,0,0,0.55)" }}
              >
                位置情報が取れないユーザー（{orphanBuckets.reduce(
                  (s, b) => s + b.users.length,
                  0
                )} 人）：
                {orphanBuckets
                  .map(
                    (b) => `${b.label || "未登録"} (${b.users.length})`
                  )
                  .join(" / ")}
              </Typography>
            </Box>
          )}
        </Box>
        {dataLoading ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ height: { xs: 400, md: 600 }, width: "100%" }}>
              <MapContainer
                key={`${mode}-${regionKey}-${open}`}
                center={center}
                zoom={zoom}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {placedBuckets.map((b) => {
                  const fill = colorForCount(b.users.length);
                  return (
                  <CircleMarker
                    key={b.key}
                    center={[b.lat, b.lng]}
                    radius={markerRadius(b.users.length)}
                    pathOptions={{
                      color: "#7a4a00",
                      fillColor: fill,
                      fillOpacity: 0.8,
                      weight: 1.5,
                    }}
                  >
                    <Popup maxWidth={360}>
                      <Box sx={{ minWidth: 220 }}>
                        <Typography
                          sx={{ fontWeight: 800, color: "rgba(0,0,0,0.85)" }}
                        >
                          {b.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(0,0,0,0.6)" }}
                        >
                          {b.users.length} 人
                        </Typography>
                        <Divider sx={{ my: 0.75 }} />
                        <Stack
                          spacing={0.25}
                          sx={{ maxHeight: 240, overflowY: "auto" }}
                        >
                          {b.users.map((u) => {
                            const ai = assignedToGroup.get(u.id);
                            return (
                              <Stack
                                key={u.id}
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                sx={{ opacity: ai ? 0.55 : 1 }}
                              >
                                <Avatar
                                  src={u.avatar || undefined}
                                  alt={u.name || "user"}
                                  sx={{ width: 22, height: 22, fontSize: 11 }}
                                >
                                  {(u.name || "?").slice(0, 1)}
                                </Avatar>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "rgba(0,0,0,0.85)",
                                  }}
                                  noWrap
                                >
                                  {u.name || "(名前未設定)"}
                                </Typography>
                                {ai && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "rgba(0,0,0,0.55)" }}
                                  >
                                    [{ai.groupName}]
                                  </Typography>
                                )}
                              </Stack>
                            );
                          })}
                        </Stack>
                      </Box>
                    </Popup>
                  </CircleMarker>
                  );
                })}
              </MapContainer>
            </Box>

            {/* 凡例（下に sticky） */}
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{
                px: 2,
                py: 1,
                borderTop: "1px solid rgba(0,0,0,0.08)",
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                bgcolor: "#fff",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "rgba(0,0,0,0.6)", fontWeight: 700 }}
              >
                人数：
              </Typography>
              {COUNT_TIERS.map((t) => (
                <Stack
                  key={t.label}
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      bgcolor: t.color,
                      border: "1px solid rgba(122,74,0,0.5)",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(0,0,0,0.65)" }}
                  >
                    {t.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
}
